import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ListSubheader from "@mui/material/ListSubheader";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import { useFormik } from "formik";
import * as Yup from "yup";
import Sidebar from "./comman/Sidebar";
import Navbar from "./comman/Navbar";
import { useToast } from "./comman/CustomMessages";
import personIcon from "../images/person-daily-entry.svg";
import { authHeader, getLogUserId } from "./comman/MainFunction";
import personListIcon from "../images/person-list.svg";
import arrowGreen from "../images/arrow-green.png";
import downloadIcon from "../images/download1.svg";
import editDailyIcon from "../images/edit-icon-daily-time.svg";
import deleteDailyIcon from "../images/delete-icon-daily-time.svg";
import cancelIcon from "../images/cancel-icon.svg";
import addBillEditIcon from "../images/add-bill-edit-icon.svg";
import API from "./comman/API";
import { deCryptFun, enCryptFun } from "./comman/CustomFunction";
import {
  DELETE_BILLING_STATEMENT_BY_ID,
  DELETE_ATTACHMENT_LOG_BY_ID_URL,
  GET_ATTACHMENT_LOG_LIST_URL,
  SYSTEM_LOG_INSERT_URL,
  UPLOAD_ATTACHMENT_LOG_URL,
} from "../constants/ApiRoute";
import { getUploadErrorMessage, uploadAttachmentFiles } from "./model/attachmentUpload";
import {
  buildDayMetaFromApiRecords,
  billTypeList,
  buildCalendarDays,
  buildDayMeta,
  buildWeekDays,
  calculateLineTotal,
  calculatePerDiem,
  calculateTotals,
  createEmptyLineItem,
  DAILY_HOUR_LIMIT,
  formatCurrency,
  formatDate,
  formatDisplayDate,
  formatHoursSummary,
  getBillingPeriodForDate,
  getCyclePeriodForDate,
  getAdjacentCycleDate,
  getWeekRange,
  parseNumber,
  mapApiDetailToItem,
  monthNames,
  toPeriodKey,
  weekDays,
} from "./DailyEntry/dailyEntryUtils";
import {
  checkStatementExistsForDate,
  fetchDailyEntryBilling,
  fetchDailyEntryBillingByDay,
  fetchBillingStatementDetails,
  fetchAssignedBillingProjects,
  getProjectById,
  fetchProjectsForCustomer,
  findBillingStatement,
  parseBillingApiError,
  upsertDailyEntryStatement,
} from "./DailyEntry/dailyEntryService";
import { downloadAllAttachments } from "../utils/attachmentUtils";
import { Stack, Typography } from "@mui/material";
import { EditIcon, UploadDeleteIcon } from "../images/SvgIcons";
const moment = require("moment");

const isStatementLocked = (statusId) =>
  [2, 3, 4].includes(Number.parseInt(statusId, 10));

const createValidationSchema = (existingStatementProjectId = "") =>
  Yup.object({
    customerId: Yup.string().required("Customer is required"),
    projectId: Yup.string().test(
      "project-required",
      "Project is required",
      function validateProjectId(value) {
        return Boolean(
          String(value || existingStatementProjectId || "").trim(),
        );
      },
    ),
    items: Yup.array().of(
      Yup.object({
        billingType: Yup.string().required("Billing type is required"),
      }),
    ),
  });

const createBlankLineItem = (dateKey) => createEmptyLineItem(dateKey);

const getDefaultForm = (date) => ({
  customerId: "",
  projectId: "",
  items: [createBlankLineItem(formatDate(date))],
});

const footerActionBoxStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap",
  padding: "10px 0px",
  // border: "1px solid #e3e8ef",
  // borderRadius: "8px",
  // background: "#fbfcfe",
};

const resolveStatusId = (source) => {
  const raw =
    source?.StatusID ??
    source?.StatusId ??
    source?.statusId ??
    source?.statusID;
  const parsed = Number.parseInt(raw, 10);
  if (Number.isFinite(parsed)) return parsed;

  // Some endpoints return a status label instead of a numeric ID (e.g. "Statement Closed").
  const label = String(source?.Status ?? source?.status ?? "")
    .trim()
    .toLowerCase();
  if (!label) return 0;
  if (label.includes("statement open")) return 1;
  if (label.includes("statement closed")) return 2;
  if (label.includes("invoice started") || label.includes("invoice stated"))
    return 3;
  if (label.includes("invoice closed")) return 4;

  return 0;
};

const getStatusChip = (statusId) => {
  switch (Number(statusId)) {
    case 1:
      return { label: "Statement Open", className: "invoice-open" };
    case 2:
      return { label: "Statement Closed", className: "invoice-ready" };
    case 3:
      return { label: "Invoice Started", className: "invoice-started" };
    case 4:
      return { label: "Invoice Closed", className: "invoice-closed" };
    default:
      return { label: "Statement Open", className: "invoice-open" };
  }
};

const isDeleteBlockedStatus = (statusId) => isStatementLocked(statusId);

const DailyEntryPage = () => {
  const showToast = useToast();
  const [today] = useState(() => new Date());
  const todayKey = useMemo(() => formatDate(today), [today]);
  const [selectedDate, setSelectedDate] = useState(today);
  const [currentMonthDate, setCurrentMonthDate] = useState(today);
  const [viewMode, setViewMode] = useState("cycle");
  const [isBillingOpen, setIsBillingOpen] = useState(false);
  const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [popupLoading, setPopupLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [projectRateOverride, setProjectRateOverride] = useState(null);
  const [statementContext, setStatementContext] = useState(null);
  const [dayEntriesMeta, setDayEntriesMeta] = useState({});
  const [formSeed, setFormSeed] = useState(getDefaultForm(today));
  // Snapshot of the last hydrated popup state so Cancel/re-open can restore API values
  // without relying on a full page refresh.
  const [originalPopupState, setOriginalPopupState] = useState(null);
  const [attachmentFile, setAttachmentFile] = useState([]);
  const [attachmentName, setAttachmentName] = useState([]);
  const [attachmentRemark, setAttachmentRemark] = useState("");
  const [attachmentFileObject, setAttachmentFileObject] = useState([]);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  const [attachmentActionLoading, setAttachmentActionLoading] = useState(false);
  const [downloadAllLoading, setDownloadAllLoading] = useState(false);
  const [attachmentEditId, setAttachmentEditId] = useState(0);
  const [, setAttachmentFilePath] = useState("");
  const [selectedAttachmentId, setSelectedAttachmentId] = useState(null);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [savedDaySections, setSavedDaySections] = useState([]);
  const [popupSummary, setPopupSummary] = useState({ hours: 0, amount: 0 });
  const [attachmentContextBackup, setAttachmentContextBackup] = useState(null);
  const [deletingDetailId, setDeletingDetailId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPopupRefreshing, setIsPopupRefreshing] = useState(false);
  const [isAddingMore, setIsAddingMore] = useState(false);
  const [showManualSection, setShowManualSection] = useState(false);
  const [draftSectionTouched, setDraftSectionTouched] = useState({});
  const [initialRatesByProject, setInitialRatesByProject] = useState({});
  const manualRateOverrideRef = useRef({
    dayRate: false,
    mileageRate: false,
  });
  const projectDetailsCacheRef = useRef(new Map());
  const projectDetailsPromiseRef = useRef(new Map());
  const sectionKeySeedRef = useRef(0);
  // Cycle controls the calendar view only. Statement metadata must continue
  // using the selected project's actual billing period.
  const resolveActivePeriod = (date, billingPeriod) =>
    getBillingPeriodForDate(date, billingPeriod);
  const shouldValidateStandaloneForm =
    !Array.isArray(savedDaySections) ||
    savedDaySections.length === 0 ||
    showManualSection;
  const formatRate = (value) => parseNumber(value).toFixed(2);
  const normalizeRateNumber = (value) => Number(parseNumber(value).toFixed(2));
  const createClientSectionKey = (projectId = "project") => {
    sectionKeySeedRef.current += 1;
    return `section-${String(projectId || "project")}-${Date.now()}-${sectionKeySeedRef.current}`;
  };
  const getProjectDetailsCached = async (projectId, options = {}) => {
    const key = String(projectId || "");
    if (!key) return null;
    const forceRefresh = Boolean(options?.forceRefresh);

    if (forceRefresh) {
      projectDetailsCacheRef.current.delete(key);
      projectDetailsPromiseRef.current.delete(key);
    }

    if (projectDetailsCacheRef.current.has(key)) {
      return projectDetailsCacheRef.current.get(key);
    }
    if (projectDetailsPromiseRef.current.has(key)) {
      return projectDetailsPromiseRef.current.get(key);
    }

    const request = getProjectById(projectId)
      .then((response) => {
        projectDetailsCacheRef.current.set(key, response);
        projectDetailsPromiseRef.current.delete(key);
        return response;
      })
      .catch((error) => {
        projectDetailsPromiseRef.current.delete(key);
        throw error;
      });

    projectDetailsPromiseRef.current.set(key, request);
    return request;
  };

  const formik = useFormik({
    initialValues: formSeed,
    enableReinitialize: true,
    validationSchema: shouldValidateStandaloneForm
      ? createValidationSchema(String(statementContext?.projectId || ""))
      : null,
    onSubmit: async (values) => {
      if (isDeleting) return;
      if (isStatementLocked(resolveStatusId(statementContext?.statement))) {
        showToast("This statement is closed and cannot be modified.", "error");
        return;
      }
      setSaving(true);

      try {
        const dateKey = formatDate(selectedDate);
        const statementDetailsCache = new Map();
        const projectRateCache = new Map();
        const discrepancyWarning = {
          dayRateChanged: false,
          mileageRateChanged: false,
          officePerDiemChanged: false,
          travelPerDiemChanged: false,
        };

        const resolveProjectPayloadRates = async (
          projectId,
          fallbackProject,
        ) => {
          const key = String(projectId || "");
          if (!key) {
            return {
              dayRate: 0,
              mileageRate: 0,
              officePerDiem: 0,
              travelPerDiem: 0,
            };
          }
          if (projectRateCache.has(key)) return projectRateCache.get(key);

          let resolvedDayRate = parseNumber(
            fallbackProject?.BillingDayRate ??
              fallbackProject?.DayRate ??
              fallbackProject?.dayRate ??
              0,
          );
          let resolvedMileageRate = parseNumber(
            fallbackProject?.MileageRate ?? fallbackProject?.mileageRate ?? 0,
          );
          let resolvedOfficePerDiem = parseNumber(
            fallbackProject?.BillableOfficePerDiem ??
              fallbackProject?.PayableOfficePerDiem ??
              fallbackProject?.OfficePerDiem ??
              fallbackProject?.officePerDiem ??
              fallbackProject?.OfficePerDiemRate ??
              fallbackProject?.officePerDiemRate ??
              0,
          );
          let resolvedTravelPerDiem = parseNumber(
            fallbackProject?.BillableTravelPerDiem ??
              fallbackProject?.PayableTravelPerDiem ??
              fallbackProject?.TravelPerDiem ??
              fallbackProject?.travelPerDiem ??
              fallbackProject?.TravelPerDiemRate ??
              fallbackProject?.travelPerDiemRate ??
              0,
          );

          try {
            const projectResponse = await getProjectDetailsCached(projectId, {
              forceRefresh: true,
            });
            const projectDetails = projectResponse?.ProjectDetails?.[0] || {};
            const loggedInUserId = getLogUserId();
            const brokerDetails = (projectResponse?.BrokerDetails || []).find(
              (item) => String(item?.UserID) === String(loggedInUserId),
            );

            const dayRateFromProjectDetails = parseNumber(
              projectDetails?.BillingDayRate ??
                projectDetails?.DayRate ??
                projectDetails?.dayRate ??
                0,
            );
            const dayRateFromBrokerDetails = parseNumber(
              brokerDetails?.DayRate ??
                brokerDetails?.BillingDayRate ??
                brokerDetails?.dayRate ??
                brokerDetails?.billingDayRate ??
                0,
            );
            const mileageFromProjectDetails = parseNumber(
              projectDetails?.MileageRate ?? projectDetails?.mileageRate ?? 0,
            );
            const officePerDiemFromBrokerDetails = parseNumber(
              brokerDetails?.PayableOfficePerDiem ??
                brokerDetails?.payableOfficePerDiem ??
                brokerDetails?.OfficePerDiem ??
                brokerDetails?.officePerDiem ??
                brokerDetails?.OfficePerDiemRate ??
                brokerDetails?.officePerDiemRate ??
                0,
            );
            const officePerDiemFromProjectDetails = parseNumber(
              projectDetails?.BillableOfficePerDiem ??
                projectDetails?.billableOfficePerDiem ??
                projectDetails?.PayableOfficePerDiem ??
                projectDetails?.payableOfficePerDiem ??
                projectDetails?.OfficePerDiem ??
                projectDetails?.officePerDiem ??
                projectDetails?.OfficePerDiemRate ??
                projectDetails?.officePerDiemRate ??
                0,
            );
            const travelPerDiemFromBrokerDetails = parseNumber(
              brokerDetails?.PayableTravelPerDiem ??
                brokerDetails?.payableTravelPerDiem ??
                brokerDetails?.TravelPerDiem ??
                brokerDetails?.travelPerDiem ??
                brokerDetails?.TravelPerDiemRate ??
                brokerDetails?.travelPerDiemRate ??
                0,
            );
            const travelPerDiemFromProjectDetails = parseNumber(
              projectDetails?.BillableTravelPerDiem ??
                projectDetails?.billableTravelPerDiem ??
                projectDetails?.PayableTravelPerDiem ??
                projectDetails?.payableTravelPerDiem ??
                projectDetails?.TravelPerDiem ??
                projectDetails?.travelPerDiem ??
                projectDetails?.TravelPerDiemRate ??
                projectDetails?.travelPerDiemRate ??
                0,
            );
            if (dayRateFromBrokerDetails) {
              resolvedDayRate = dayRateFromBrokerDetails;
            } else if (dayRateFromProjectDetails) {
              resolvedDayRate = dayRateFromProjectDetails;
            }
            if (mileageFromProjectDetails) {
              resolvedMileageRate = mileageFromProjectDetails;
            }
            if (officePerDiemFromBrokerDetails) {
              resolvedOfficePerDiem = officePerDiemFromBrokerDetails;
            } else if (officePerDiemFromProjectDetails) {
              resolvedOfficePerDiem = officePerDiemFromProjectDetails;
            }
            if (travelPerDiemFromBrokerDetails) {
              resolvedTravelPerDiem = travelPerDiemFromBrokerDetails;
            } else if (travelPerDiemFromProjectDetails) {
              resolvedTravelPerDiem = travelPerDiemFromProjectDetails;
            }
          } catch (error) {
            // fallback to already resolved value
          }

          const resolved = {
            dayRate: resolvedDayRate,
            mileageRate: resolvedMileageRate,
            officePerDiem: resolvedOfficePerDiem,
            travelPerDiem: resolvedTravelPerDiem,
          };
          projectRateCache.set(key, resolved);
          return resolved;
        };

        const buildStatementDetailsPayload = async ({
          customerId,
          projectId,
          items,
          statement,
          project,
        }) => {
          if (!projectId) return null;

          let resolvedProject =
            project ||
            availableProjects.find(
              (item) => String(item.ProjectId) === String(projectId),
            ) ||
            null;

          if (!resolvedProject) {
            try {
              const projectResponse = await getProjectDetailsCached(projectId);
              resolvedProject = projectResponse?.ProjectDetails?.[0] || null;
            } catch (error) {
              resolvedProject = null;
            }
          }

          const period = resolveActivePeriod(
            selectedDate,
            resolvedProject?.BillingPeriod,
          );
          const resolvedStatementPeriod = resolveStatementPeriod(
            statement,
            period,
          );

          const statusId = resolveStatusId(statement) || 1;
          const statementIdSource =
            statement?.billingStatementId ??
            statement?.BillingStatementId ??
            statement?.BillingStatementID ??
            statement?.HeaderID ??
            statement?.headerId ??
            statement?.ID ??
            statement?.Id ??
            statement?.id ??
            0;
          const billingStatementId = Number.parseInt(
            statementIdSource || 0,
            10,
          );
          const headerId = Number.isFinite(billingStatementId)
            ? billingStatementId
            : 0;

          const projectRates = await resolveProjectPayloadRates(
            projectId,
            resolvedProject,
          );
          // Keep payload dayRate consistent with the resolved display rate:
          // broker/project day rate first, then line-item fallback.
          const uiDayRate = parseNumber(items?.[0]?.dayRate);
          const dayRate =
            parseNumber(projectRates.dayRate) ||
            parseNumber(
              resolvedProject?.BillingDayRate ??
                resolvedProject?.DayRate ??
              resolvedProject?.dayRate ??
                0,
            ) ||
            uiDayRate;
          const mileageRate = parseNumber(projectRates.mileageRate);
          const officePerDiem = parseNumber(projectRates.officePerDiem);
          const travelPerDiem = parseNumber(projectRates.travelPerDiem);
          const initialRates =
            initialRatesByProject?.[String(projectId)] || null;
          if (initialRates) {
            const initialDayRate = Number.parseFloat(initialRates?.dayRate);
            const currentDayRate = Number.parseFloat(dayRate);
            const initialMileageRate = Number.parseFloat(
              initialRates?.mileageRate,
            );
            const currentMileageRate = Number.parseFloat(mileageRate);
            const initialOfficePerDiem = Number.parseFloat(
              initialRates?.officePerDiem,
            );
            const currentOfficePerDiem = Number.parseFloat(officePerDiem);
            const initialTravelPerDiem = Number.parseFloat(
              initialRates?.travelPerDiem,
            );
            const currentTravelPerDiem = Number.parseFloat(travelPerDiem);
            const hasDayRateChanged =
              Number.isFinite(initialDayRate) &&
              Number.isFinite(currentDayRate) &&
              Math.abs(initialDayRate - currentDayRate) > 0.001;
            const hasMileageRateChanged =
              Number.isFinite(initialMileageRate) &&
              Number.isFinite(currentMileageRate) &&
              Math.abs(initialMileageRate - currentMileageRate) > 0.001;
            const hasOfficePerDiemChanged =
              Number.isFinite(initialOfficePerDiem) &&
              Number.isFinite(currentOfficePerDiem) &&
              Math.abs(initialOfficePerDiem - currentOfficePerDiem) > 0.001;
            const hasTravelPerDiemChanged =
              Number.isFinite(initialTravelPerDiem) &&
              Number.isFinite(currentTravelPerDiem) &&
              Math.abs(initialTravelPerDiem - currentTravelPerDiem) > 0.001;

            if (hasDayRateChanged) {
              discrepancyWarning.dayRateChanged = true;
            }
            if (hasMileageRateChanged) {
              discrepancyWarning.mileageRateChanged = true;
            }
            if (hasOfficePerDiemChanged) {
              discrepancyWarning.officePerDiemChanged = true;
            }
            if (hasTravelPerDiemChanged) {
              discrepancyWarning.travelPerDiemChanged = true;
            }
          }

          const filteredItems = (Array.isArray(items) ? items : []).filter(
            (item) =>
              item?.billingType &&
              formatApiDateKey(item?.date, dateKey) === dateKey,
          );
          const selectedDayItems = filteredItems.map((item) => ({
            ...item,
            dayRate: parseNumber(item.dayRate)
              ? item.dayRate
              : dayRate.toFixed(2),
            mileageRate: parseNumber(item.mileageRate)
              ? item.mileageRate
              : mileageRate.toFixed(2),
            perDiem: calculatePerDiem(item.hours, item.perDiemRate),
            date: formatApiDateKey(item?.date, dateKey),
          }));

          const headerDetails = [];
          if (headerId > 0) {
            const cacheKey = String(headerId);
            if (!statementDetailsCache.has(cacheKey)) {
              const fetched = await fetchBillingStatementDetails(headerId);
              statementDetailsCache.set(
                cacheKey,
                Array.isArray(fetched) ? fetched : [],
              );
            }

            const rawDetails = statementDetailsCache.get(cacheKey) || [];
            rawDetails.forEach((detail) => {
              const detailDate = formatApiDateKey(
                detail?.Date ?? detail?.date,
                dateKey,
              );
              const detailHours = parseNumber(detail?.Hours ?? detail?.hours);
              const detailPerDiemRate = parseNumber(
                detail?.PerDiemRate ?? detail?.perDiemRate,
              );
              headerDetails.push({
                billingType: String(
                  detail?.BillingType ??
                    detail?.billingType ??
                    detail?.BillingTypeId ??
                    detail?.billingTypeId ??
                    "",
                ),
                dayRate: parseNumber(detail?.DayRate ?? detail?.dayRate)
                  ? parseNumber(detail?.DayRate ?? detail?.dayRate).toFixed(2)
                  : dayRate.toFixed(2),
                hours: detailHours.toFixed(2),
                miles: parseNumber(detail?.Miles ?? detail?.miles).toString(),
                mileageRate: parseNumber(
                  detail?.MileageRate ?? detail?.mileageRate,
                )
                  ? parseNumber(
                      detail?.MileageRate ?? detail?.mileageRate,
                    ).toFixed(2)
                  : mileageRate.toFixed(2),
                lodging: parseNumber(
                  detail?.Lodging ?? detail?.lodging,
                ).toFixed(2),
                copies: parseNumber(detail?.Copies ?? detail?.copies).toFixed(
                  2,
                ),
                description: detail?.Description ?? detail?.description ?? "",
                perDiemRate: detailPerDiemRate.toFixed(2),
                perDiem: parseNumber(
                  detail?.PerDiem ??
                    detail?.perDiem ??
                    calculatePerDiem(detailHours, detailPerDiemRate),
                ).toFixed(2),
                misc: parseNumber(detail?.Misc ?? detail?.misc).toFixed(2),
                date: detailDate,
                billingStatementDetailsId:
                  detail?.billingStatementDetailsId ??
                  detail?.BillingStatementDetailsId ??
                  0,
                isTravelPerDiem: resolveApiBoolean(
                  detail?.IsTravelPerDiem ??
                    detail?.isTravelPerDiem ??
                    detail?.sTravelPerDiem ??
                    detail?.sTravelPerdiem,
                ),
                isOfficePerDiem: resolveApiBoolean(
                  detail?.IsOfficePerDiem ??
                    detail?.isOfficePerDiem ??
                    detail?.sOfficePerDiem ??
                    detail?.sOfficePerdiem,
                ),
              });
            });
          }

          const remainingPeriodItems = headerDetails.filter(
            (item) => formatApiDateKey(item?.date, dateKey) !== dateKey,
          );
          const calcItems = [...remainingPeriodItems, ...selectedDayItems];

          if (calcItems.length === 0) return null;

          const sum = (field) =>
            calcItems.reduce(
              (acc, item) => acc + parseNumber(item?.[field]),
              0,
            );
          const totalHours = sum("hours");
          const totalPerDiem = sum("perDiem");
          const totalMiles = sum("miles");
          const totalLodging = sum("lodging");
          const totalMisc = sum("misc");
          const totalCopies = sum("copies");
          // Keep payload total consistent with UI total formula:
          // PerDiem + Lodging + Copies + Misc + (Miles * MileageRate) + ((min(Hours,8)/8) * DayRate)
          const totalAmount = calcItems.reduce(
            (acc, item) => acc + calculateLineTotal(item),
            0,
          );

          return {
            billingStatementId: headerId || 0,
            userId: getLogUserId(),
            periodKey: resolvedStatementPeriod.periodKey || period.periodKey,
            projectId: Number.parseInt(projectId, 10) || projectId,
            statusId: statusId || 1,
            billingStart:
              resolvedStatementPeriod.billingStart || period.billingStart,
            billingEnd: resolvedStatementPeriod.billingEnd || period.billingEnd,
            hours: totalHours,
            dayRate: Number(dayRate.toFixed(2)),
            perDiem: totalPerDiem,
            lodging: totalLodging,
            miles: totalMiles,
            misc: totalMisc,
            copies: totalCopies,
            mileageRate: Number(mileageRate),
            total: Number(totalAmount.toFixed(2)),
            isOrphaned: false,
            billingDetails: calcItems.map((item) => ({
              billingStatementDetailsId: item.billingStatementDetailsId || 0,
              headerId: headerId || 0,
              billingType: Number(item.billingType),
              date: formatApiDateKey(item?.date, dateKey),
              hours: Number(parseNumber(item.hours)),
              perDiemRate: Number(parseNumber(item.perDiemRate)),
              perDiem: Number(parseNumber(item.perDiem)),
              lodging: Number(parseNumber(item.lodging)),
              miles: Number(parseNumber(item.miles)),
              copies: Number(parseNumber(item.copies)),
              misc: Number(parseNumber(item.misc)),
              description: item.description || "",
              isOrphaned: 0,
              isTravelPerDiem: item.isTravelPerDiem ? true : false,
              isOfficePerDiem: item.isOfficePerDiem ? true : false,
            })),
          };
        };

        const statementDetails = [];

        // Current editable form (single-project)
        const currentProject = availableProjects.find(
          (project) => String(project.ProjectId) === String(values.projectId),
        );
        const currentPayload = await buildStatementDetailsPayload({
          customerId: values.customerId,
          projectId: values.projectId,
          items: values.items,
          statement: statementContext?.statement,
          project: currentProject,
        });
        if (currentPayload) statementDetails.push(currentPayload);

        // Any other open statements present in saved sections (multi-project support)
        for (const section of savedDaySections || []) {
          if (resolveStatusId(section?.statement) !== 1) continue;
          const payload = await buildStatementDetailsPayload({
            customerId: section.customerId,
            projectId: section.projectId,
            items: section.items,
            statement: section.statement,
            project: null,
          });
          if (payload) statementDetails.push(payload);
        }

        const buildFinalStatementPayload = (entries = []) =>
          Array.from(
            (Array.isArray(entries) ? entries : [])
              .reduce((acc, current) => {
                const statementId = Number(current?.billingStatementId || 0);
                const rawProjectId = current?.projectId ?? "";
                const projectId =
                  Number.parseInt(rawProjectId, 10) || rawProjectId;
                const currentPeriodKey = String(current?.periodKey || "");
                const periodFromKey = parsePeriodKeyRange(currentPeriodKey);
                const effectivePeriodKey =
                  currentPeriodKey ||
                  (periodFromKey?.billingStart && periodFromKey?.billingEnd
                    ? toPeriodKey(
                        parseUsDateStrict(periodFromKey.billingStart),
                        parseUsDateStrict(periodFromKey.billingEnd),
                      )
                    : "");

                const mergeKey =
                  statementId !== 0
                    ? `stmt_${statementId}`
                    : `proj_${String(projectId)}_${effectivePeriodKey}`;
                let existing = acc.get(mergeKey);

                if (!existing && statementId === 0) {
                  const projectScopedExistingKey = Array.from(acc.keys()).find(
                    (key) => {
                      const candidate = acc.get(key);
                      if (!candidate) return false;

                      const candidateProjectId =
                        Number.parseInt(candidate?.projectId, 10) ||
                        candidate?.projectId;
                      const candidateStatementId = Number(
                        candidate?.billingStatementId || 0,
                      );

                      return (
                        candidateStatementId > 0 &&
                        String(candidateProjectId) === String(projectId)
                      );
                    },
                  );

                  if (projectScopedExistingKey) {
                    existing = acc.get(projectScopedExistingKey);
                  }
                }

                if (!existing) {
                  acc.set(mergeKey, {
                    ...current,
                    billingStatementId: statementId || 0,
                    projectId,
                    periodKey: effectivePeriodKey,
                    billingStart: sanitizeUsDateString(
                      current?.billingStart,
                      periodFromKey?.billingStart || "",
                    ),
                    billingEnd: sanitizeUsDateString(
                      current?.billingEnd,
                      periodFromKey?.billingEnd || "",
                    ),
                    billingDetails: Array.isArray(current?.billingDetails)
                      ? [...current.billingDetails]
                      : [],
                  });
                  return acc;
                }

                existing.billingStatementId =
                  Number(existing.billingStatementId || 0) || statementId || 0;
                existing.statusId =
                  Number(existing.statusId || 0) ||
                  Number(current?.statusId || 0) ||
                  1;
                existing.periodKey = existing.periodKey || effectivePeriodKey;

                const existingStartDate = parseUsDateStrict(
                  existing.billingStart,
                );
                const existingEndDate = parseUsDateStrict(existing.billingEnd);
                const currentStartDate = parseUsDateStrict(
                  current?.billingStart,
                );
                const currentEndDate = parseUsDateStrict(current?.billingEnd);

                if (
                  currentStartDate &&
                  (!existingStartDate || currentStartDate < existingStartDate)
                ) {
                  existing.billingStart = formatUsDate(currentStartDate);
                }
                if (
                  currentEndDate &&
                  (!existingEndDate || currentEndDate > existingEndDate)
                ) {
                  existing.billingEnd = formatUsDate(currentEndDate);
                }

                if (
                  !parseNumber(existing.dayRate) &&
                  parseNumber(current.dayRate)
                ) {
                  existing.dayRate = current.dayRate;
                }
                if (
                  !parseNumber(existing.mileageRate) &&
                  parseNumber(current.mileageRate)
                ) {
                  existing.mileageRate = current.mileageRate;
                }

                existing.billingDetails = [
                  ...(existing.billingDetails || []),
                  ...(Array.isArray(current?.billingDetails)
                    ? current.billingDetails
                    : []),
                ];
                const targetKey =
                  Number(existing?.billingStatementId || 0) > 0
                    ? `stmt_${Number(existing.billingStatementId || 0)}`
                    : mergeKey;
                acc.set(targetKey, existing);
                return acc;
              }, new Map())
              .values(),
          ).map((entry) => {
            const dayRate = parseNumber(entry?.dayRate);
            const mileageRate = parseNumber(entry?.mileageRate);
            const headerId = Number(entry?.billingStatementId || 0);
            const rawBillingDetails = (entry?.billingDetails || []).map(
              (detail) => ({
                ...detail,
                headerId,
                hours: Number(parseNumber(detail?.hours)),
                perDiemRate: Number(parseNumber(detail?.perDiemRate)),
                perDiem: Number(parseNumber(detail?.perDiem)),
                lodging: Number(parseNumber(detail?.lodging)),
                miles: Number(parseNumber(detail?.miles)),
                copies: Number(parseNumber(detail?.copies)),
                misc: Number(parseNumber(detail?.misc)),
              }),
            );

            // Keep one instance for persisted details; when duplicate IDs exist,
            // the latest detail wins (avoids duplicated billingStatementDetailsId in payload).
            const persistedById = new Map();
            const nonPersisted = [];
            rawBillingDetails.forEach((detail) => {
              const detailId = Number(detail?.billingStatementDetailsId || 0);
              if (detailId > 0) {
                persistedById.set(detailId, detail);
                return;
              }
              nonPersisted.push(detail);
            });
            const billingDetails = [...persistedById.values(), ...nonPersisted];

            const totals = billingDetails.reduce(
              (acc, detail) => {
                const hours = parseNumber(detail?.hours);
                const perDiem = parseNumber(detail?.perDiem);
                const lodging = parseNumber(detail?.lodging);
                const copies = parseNumber(detail?.copies);
                const misc = parseNumber(detail?.misc);
                const miles = parseNumber(detail?.miles);
                const hoursCal = Math.min(hours, DAILY_HOUR_LIMIT);
                const lineTotal =
                  perDiem +
                  lodging +
                  copies +
                  misc +
                  miles * mileageRate +
                  (hoursCal / DAILY_HOUR_LIMIT) * dayRate;

                acc.hours += hours;
                acc.perDiem += perDiem;
                acc.lodging += lodging;
                acc.miles += miles;
                acc.misc += misc;
                acc.copies += copies;
                acc.total += lineTotal;
                return acc;
              },
              {
                hours: 0,
                perDiem: 0,
                lodging: 0,
                miles: 0,
                misc: 0,
                copies: 0,
                total: 0,
              },
            );

            return {
              ...entry,
              billingStatementId: headerId,
              billingStart: sanitizeUsDateString(entry?.billingStart, ""),
              billingEnd: sanitizeUsDateString(entry?.billingEnd, ""),
              hours: totals.hours,
              perDiem: totals.perDiem,
              lodging: totals.lodging,
              miles: totals.miles,
              misc: totals.misc,
              copies: totals.copies,
              total: Number(totals.total.toFixed(2)),
              billingDetails,
            };
          });
        const mergedStatementDetails =
          buildFinalStatementPayload(statementDetails);

        if (mergedStatementDetails.length === 0) {
          showToast(
            "Please add at least one billing line item to save.",
            "error",
          );
          return;
        }

        if (
          discrepancyWarning.dayRateChanged ||
          discrepancyWarning.mileageRateChanged ||
          discrepancyWarning.officePerDiemChanged ||
          discrepancyWarning.travelPerDiemChanged
        ) {
          showToast(
            "There are discrepancies in the broker's payables, and the new payable is applied.",
            "dayRateWarning",
          );
        }

        await upsertDailyEntryStatement({
          statementDetails: mergedStatementDetails,
        });

        await refreshCalendarEntries({
          anchorDate: selectedDate,
          mode: viewMode,
        });

        await hydratePopup(selectedDate, values.customerId, values.projectId);
        manualRateOverrideRef.current = { dayRate: false, mileageRate: false };
        showToast("Billing entry saved successfully.", "success");
      } catch (error) {
        const apiError = parseBillingApiError(error);
        showToast(
          apiError?.message?.message ||
            apiError?.message ||
            "Unable to save billing entry.",
          "error",
        );
        setIsPopupRefreshing(true);
        await refreshCalendarEntries({
          anchorDate: selectedDate,
          mode: viewMode,
        });
        await hydratePopup(
          selectedDate,
          values.customerId || formik.values.customerId,
          values.projectId || formik.values.projectId,
        );
      } finally {
        setIsPopupRefreshing(false);
        setSaving(false);
      }
    },
  });
  const latestFormikValuesRef = useRef(formik.values);

  useEffect(() => {
    latestFormikValuesRef.current = formik.values;
  }, [formik.values]);
  useEffect(() => {
    let active = true;

    const loadAssignments = async () => {
      setLoadingAssignments(true);

      try {
        const response = await fetchAssignedBillingProjects();
        if (!active) return;

        setCustomers(response.customers);
        setAssignedProjects(response.assignedProjects);

        if (response.customers.length > 0) {
          const firstCustomerId = String(response.customers[0].CustomerID);
          const projects = await fetchProjectsForCustomer(
            firstCustomerId,
            response.assignedProjects,
          );
          if (!active) return;

          setAvailableProjects(projects);
          setFormSeed({
            customerId: firstCustomerId,
            projectId: projects[0] ? String(projects[0].ProjectId) : "",
            items: [createBlankLineItem(formatDate(today))],
          });
        }
      } catch (error) {
        const apiError = parseBillingApiError(error);
        showToast(
          apiError?.message?.message ||
            apiError?.message ||
            "Unable to load daily entry data.",
          "error",
        );
      } finally {
        if (active) setLoadingAssignments(false);
      }
    };

    loadAssignments();
    return () => {
      active = false;
    };
  }, [showToast, today]);

  const refreshCalendarEntries = async ({
    anchorDate = selectedDate,
    mode = viewMode,
  } = {}) => {
    const monthStart = new Date(
      currentMonthDate.getFullYear(),
      currentMonthDate.getMonth(),
      1,
    );
    const monthEnd = new Date(
      currentMonthDate.getFullYear(),
      currentMonthDate.getMonth() + 1,
      0,
    );
    const weekRange = getWeekRange(anchorDate);
    // Same cycle the header shows (1st-14th or 15th-month end).
    const cyclePeriod = getCyclePeriodForDate(anchorDate);
    const startDate =
      mode === "week"
        ? weekRange.startDateString
        : mode === "cycle"
          ? cyclePeriod.billingStart
          : formatDate(monthStart);
    const endDate =
      mode === "week"
        ? weekRange.endDateString
        : mode === "cycle"
          ? cyclePeriod.billingEnd
          : formatDate(monthEnd);

    setCalendarLoading(true);
    try {
      const records = await fetchDailyEntryBilling({ startDate, endDate });
      setDayEntriesMeta(buildDayMetaFromApiRecords(records));
    } catch (error) {
      const apiError = parseBillingApiError(error);
      const statusCode =
        error?.response?.status ??
        apiError?.status ??
        apiError?.response?.status;
      const message = String(
        apiError?.message?.message || apiError?.message || "",
      ).toLowerCase();
      const noRecordFound =
        statusCode === 403 && message.includes("no record found");
      if (noRecordFound) {
        setDayEntriesMeta({});
      }
      // showToast(
      //   apiError?.message?.message ||
      //     apiError?.message ||
      //     "Unable to load daily entry calendar.",
      //   "error"
      // );
    } finally {
      setCalendarLoading(false);
    }
  };

  useEffect(() => {
    if (loadingAssignments) return;
    if (isBillingOpen || isDeleting) return;
    refreshCalendarEntries();
  }, [loadingAssignments, currentMonthDate, selectedDate, viewMode]);

  const selectedDayKey = formatDate(selectedDate);
  const summarySnapshot = useMemo(() => {
    if (originalPopupState?.dateKey === selectedDayKey) {
      return originalPopupState?.popupSummary || { hours: 0, amount: 0 };
    }

    return popupSummary;
  }, [originalPopupState, popupSummary, selectedDayKey]);
  const initiatedTotals = useMemo(() => {
    const apiHours = parseNumber(summarySnapshot?.hours);
    const apiAmount = parseNumber(summarySnapshot?.amount);
    if (Number.isFinite(apiHours) && Number.isFinite(apiAmount)) {
      return { hours: apiHours, amount: apiAmount };
    }

    return { hours: 0, amount: 0 };
  }, [summarySnapshot]);

  const calendarDays = useMemo(
    () =>
      buildCalendarDays(
        currentMonthDate.getFullYear(),
        currentMonthDate.getMonth(),
        selectedDate.getDate(),
        dayEntriesMeta,
        viewMode === "cycle" ? getCyclePeriodForDate(selectedDate) : null,
      ),
    [currentMonthDate, dayEntriesMeta, selectedDate, viewMode],
  );
  const weekMeta = useMemo(
    () => buildWeekDays(selectedDate, dayEntriesMeta),
    [dayEntriesMeta, selectedDate],
  );
  const visibleSummary = useMemo(
    () =>
      (viewMode === "week"
        ? weekMeta.map((day) => day.meta).filter(Boolean)
        : Object.values(dayEntriesMeta)
      ).reduce(
        (acc, item) => {
          acc.amount +=
            parseFloat(String(item.amount || "0").replace("$", "")) || 0;
          acc.hours +=
            parseFloat(String(item.hrs || "0").replace(" Hrs", "")) || 0;
          return acc;
        },
        { hours: 0, amount: 0 },
      ),
    [dayEntriesMeta, viewMode, weekMeta],
  );
  const selectedCustomer = useMemo(
    () =>
      customers.find(
        (customer) =>
          String(customer.CustomerID) === String(formik.values.customerId),
      ) || null,
    [customers, formik.values.customerId],
  );
  const selectedProject = useMemo(
    () =>
      availableProjects.find(
        (project) =>
          String(project.ProjectId) === String(formik.values.projectId),
      ) || null,
    [availableProjects, formik.values.projectId],
  );
  useEffect(() => {
    if (!isBillingOpen) return;

    const projectId =
      statementContext?.projectId ||
      formik.values.projectId ||
      formSeed.projectId;
    if (!projectId) return;

    let active = true;
    const loadProjectDetailsForPopup = async () => {
      try {
        const response = await getProjectDetailsCached(projectId, {
          forceRefresh: true,
        });
        if (!active || !response) return;

        const rates = resolveProjectRatesFromProjectByIdResponse(response);
        setProjectRateOverride({
          projectId: String(projectId),
          dayRate: rates.dayRate,
          officePerDiem: rates.officePerDiem,
          travelPerDiem: rates.travelPerDiem,
        });
      } catch (error) {
        // Keep current popup values when project detail refresh fails.
      }
    };

    loadProjectDetailsForPopup();
    return () => {
      active = false;
    };
  }, [
    formSeed.projectId,
    formik.values.projectId,
    isBillingOpen,
    statementContext?.projectId,
  ]);
  const normalizeSavedSectionForComparison = (section = {}) => {
    const items = Array.isArray(section?.items) ? section.items : [];
    const resolvedStatusId = (() => {
      const resolved = resolveStatusId(section?.statement);
      if (resolved !== 0) return resolved;

      const fallbackRaw =
        section?.statusId ??
        section?.StatusID ??
        section?.statement?.statusId ??
        section?.statement?.StatusID;
      const fallbackParsed = Number.parseInt(fallbackRaw, 10);
      if (Number.isFinite(fallbackParsed)) return fallbackParsed;

      if (Number(section?.statement?.billingStatementId || 0) > 0) return 1;
      return 0;
    })();

    return {
      sectionKey: String(section?.sectionKey || ""),
      billingStatementId: Number(section?.statement?.billingStatementId || 0),
      statusId: Number(resolvedStatusId || 0),
      customerId: String(section?.customerId || ""),
      customerName: String(section?.customerName || ""),
      projectId: String(section?.projectId || ""),
      projectName: String(section?.projectName || ""),
      perDiemRates: {
        office: formatRate(section?.perDiemRates?.office ?? 0),
        travel: formatRate(section?.perDiemRates?.travel ?? 0),
      },
      items: items.map((item) => ({
        billingStatementDetailsId: Number(item?.billingStatementDetailsId || 0),
        billingType: String(item?.billingType || ""),
        hours: String(item?.hours ?? ""),
        miles: String(item?.miles ?? ""),
        lodging: String(item?.lodging ?? ""),
        copies: String(item?.copies ?? ""),
        misc: String(item?.misc ?? ""),
        description: String(item?.description || ""),
        perDiemRate: formatRate(item?.perDiemRate ?? 0),
        perDiem: formatRate(item?.perDiem ?? 0),
        isOfficePerDiem: Boolean(item?.isOfficePerDiem),
        isTravelPerDiem: Boolean(item?.isTravelPerDiem),
      })),
    };
  };
  const flatSavedSections = useMemo(
    () =>
      sortSectionsForDisplay(explodeSectionsByItem(savedDaySections || [])).map(
        (section, sectionIndex) => {
          const item =
            section?.items?.[0] || createBlankLineItem(selectedDayKey);
          return {
            ...section,
            item,
            items: [item],
            detailId: Number(
              item?.billingStatementDetailsId ??
                section?.billingStatementDetailsId ??
                0,
            ),
            sectionKey:
              section?.sectionKey ||
              `${section?.projectId || "project"}-${Number(
                section?.statement?.billingStatementId || 0,
              )}-${Number(item?.billingStatementDetailsId || 0) || `new-${sectionIndex}`}`,
          };
        },
      ),
    [savedDaySections, selectedDayKey],
  );
  const flatSections = flatSavedSections;
  const hasLineItems = Boolean(flatSections && flatSections.length > 0);
  const hasDraftSavedSection = flatSections.some(
    (section) => Number(section?.statement?.billingStatementId || 0) === 0,
  );
  const shouldShowStandaloneForm = !hasLineItems || showManualSection;
  const hasModifiedSavedSection = useMemo(() => {
    if (shouldShowStandaloneForm) return false;
    const originalSections =
      originalPopupState?.dateKey === formatDate(selectedDate)
        ? originalPopupState?.savedDaySections || []
        : [];
    if (!Array.isArray(savedDaySections) || savedDaySections.length === 0)
      return false;

    const normalizeSections = (sections) =>
      sortSectionsForDisplay(sections)
        .filter(
          (section) =>
            normalizeSavedSectionForComparison(section).statusId === 1,
        )
        .map((section) => normalizeSavedSectionForComparison(section));

    return (
      JSON.stringify(normalizeSections(savedDaySections)) !==
      JSON.stringify(normalizeSections(originalSections))
    );
  }, [
    originalPopupState,
    savedDaySections,
    selectedDate,
    shouldShowStandaloneForm,
  ]);
  const hasPendingEditableEntry =
    shouldShowStandaloneForm || hasDraftSavedSection || hasModifiedSavedSection;
  const visibleCardCount =
    flatSavedSections.length + (shouldShowStandaloneForm ? 1 : 0);
  const getSavedSectionStatusId = (section) => {
    const resolved = resolveStatusId(section?.statement);
    if (resolved !== 0) return resolved;

    const fallbackRaw =
      section?.statusId ??
      section?.StatusID ??
      section?.statement?.statusId ??
      section?.statement?.StatusID;
    const fallbackParsed = Number.parseInt(fallbackRaw, 10);
    if (Number.isFinite(fallbackParsed)) return fallbackParsed;

    // Fallback for legacy open statements without explicit status shape.
    if (Number(section?.statement?.billingStatementId || 0) > 0) return 1;
    return 0;
  };

  const currentStatementId = Number.parseInt(
    statementContext?.statement?.billingStatementId ?? 0,
    10,
  );
  const normalizedCurrentStatementId = Number.isFinite(currentStatementId)
    ? currentStatementId
    : 0;
  const hasSavedStatementSelection = normalizedCurrentStatementId > 0;
  const hasSavedLineItemsInForm = useMemo(
    () =>
      (formik.values.items || []).some(
        (item) => Number(item?.billingStatementDetailsId || 0) !== 0,
      ),
    [formik.values.items],
  );
  const shouldLockStandaloneHeaderSelection =
    hasSavedStatementSelection && hasSavedLineItemsInForm;

  const perDiemRates = useMemo(() => {
    const projectId = String(formik.values.projectId);
    const overrideMatches =
      projectRateOverride &&
      String(projectRateOverride.projectId) === projectId;

    const officeValue = overrideMatches
      ? projectRateOverride.officePerDiem
      : (selectedProject?.BillableOfficePerDiem ??
        selectedProject?.PayableOfficePerDiem ??
        0);

    const travelValue = overrideMatches
      ? projectRateOverride.travelPerDiem
      : (selectedProject?.BillableTravelPerDiem ??
        selectedProject?.PayableTravelPerDiem ??
        0);

    return {
      office: formatRate(officeValue),
      travel: formatRate(travelValue),
    };
  }, [
    formik.values.projectId,
    selectedProject?.ProjectId,
    projectRateOverride?.projectId,
    projectRateOverride?.officePerDiem,
    projectRateOverride?.travelPerDiem,
  ]);

  const currentStatusId = resolveStatusId(statementContext?.statement);
  const isLocked = isStatementLocked(currentStatusId);
  const isAddMoreSection =
    !hasSavedStatementSelection || normalizedCurrentStatementId === 0;
  const isStatementEditable =
    // Exception: "Add More" (new section) is always editable.
    isAddMoreSection ||
    // Existing statement is editable only when open.
    currentStatusId === 1;
  const billingAttachmentDetail = useMemo(() => {
    const resolvedProjectId =
      statementContext?.projectId || formik.values.projectId || "";
    const selectedProjectPeriod = resolveActivePeriod(
      selectedDate,
      selectedProject?.BillingPeriod,
    );
    const statusChip = getStatusChip(
      resolveStatusId(statementContext?.statement),
    );

    return {
      hStatus: statusChip.label,
      hStartDate:
        statementContext?.statement?.BillingStart ||
        statementContext?.periodLabel?.split(" - ")[0] ||
        selectedProjectPeriod.billingStart,
      hEndDate:
        statementContext?.statement?.BillingEnd ||
        statementContext?.periodLabel?.split(" - ")[1] ||
        selectedProjectPeriod.billingEnd,
      hProjectName: selectedProject?.Name || "",
      hbokerName: selectedCustomer?.CustomerName || "",
      hprojectID: resolvedProjectId,
      huserId: getLogUserId(),
      hperiodKey:
        statementContext?.statement?.PeriodKey ||
        toPeriodKey(
          selectedProjectPeriod.startDate,
          selectedProjectPeriod.endDate,
        ),
    };
  }, [
    formik.values.projectId,
    selectedCustomer,
    selectedDate,
    selectedProject,
    statementContext,
  ]);
  const findSavedStatementSectionForProject = (
    projectId,
    fallbackPeriod = null,
  ) => {
    const normalizedProjectId = String(projectId || "").trim();
    if (!normalizedProjectId) return null;

    const hasOriginalPopupState =
      originalPopupState?.dateKey === formatDate(selectedDate);
    const sourceDayDetails = hasOriginalPopupState
      ? originalPopupState?.dayDetails || []
      : [];
    const matchedDayEntry =
      sourceDayDetails.find((entry) => {
        const entryProjectId =
          entry?.ProjectId ??
          entry?.projectId ??
          entry?.ProjID ??
          entry?.projID ??
          entry?.HeaderProjectId ??
          entry?.headerProjectId ??
          "";
        const entryStatementId = Number.parseInt(
          entry?.billingStatementId ??
            entry?.BillingStatementId ??
            entry?.BillingStatementID ??
            entry?.HeaderID ??
            entry?.headerId ??
            0,
          10,
        );

        return (
          Number.isFinite(entryStatementId) &&
          entryStatementId > 0 &&
          String(entryProjectId) === normalizedProjectId
        );
      }) || null;

    if (matchedDayEntry) {
      const resolvedPeriod = resolveStatementPeriod(
        matchedDayEntry,
        fallbackPeriod || resolveActivePeriod(selectedDate),
      );
      const billingStatementId = Number.parseInt(
        matchedDayEntry?.billingStatementId ??
          matchedDayEntry?.BillingStatementId ??
          matchedDayEntry?.BillingStatementID ??
          matchedDayEntry?.HeaderID ??
          matchedDayEntry?.headerId ??
          0,
        10,
      );

      return {
        projectId: normalizedProjectId,
        periodLabel: `${resolvedPeriod.billingStart} - ${resolvedPeriod.billingEnd}`,
        statement: {
          ...matchedDayEntry,
          billingStatementId: Number.isFinite(billingStatementId)
            ? billingStatementId
            : 0,
          BillingStart: resolvedPeriod.billingStart,
          BillingEnd: resolvedPeriod.billingEnd,
          PeriodKey: resolvedPeriod.periodKey,
          StatusID: resolveStatusId(matchedDayEntry) || 1,
        },
      };
    }

    const sourceSections = hasOriginalPopupState
      ? originalPopupState?.savedDaySections || []
      : savedDaySections || [];

    return (
      explodeSectionsByItem(sourceSections).find(
        (section) =>
          Number(section?.statement?.billingStatementId || 0) > 0 &&
          String(section?.projectId || "") === normalizedProjectId,
      ) || null
    );
  };
  const markStandaloneFormTouched = () =>
    formik.setTouched(
      {
        customerId: true,
        projectId: true,
        items: (formik.values.items || []).map(() => ({
          billingType: true,
        })),
      },
      true,
    );
  const hasValidationErrors = (value) => {
    if (!value) return false;
    if (typeof value === "string") return true;
    if (Array.isArray(value))
      return value.some((item) => hasValidationErrors(item));
    if (typeof value === "object") {
      return Object.values(value).some((item) => hasValidationErrors(item));
    }
    return false;
  };
  const ensureStandaloneFormValid = async () => {
    markStandaloneFormTouched();
    const nextErrors = await formik.validateForm();
    if (hasValidationErrors(nextErrors)) {
      return false;
    }
    return true;
  };
  const getDraftSectionTouchKey = (section, sectionIndex) =>
    section?.sectionKey || `draft-${sectionIndex}`;
  const setDraftFieldTouched = (sectionIndex, field, value = true) => {
    const section =
      flatSavedSections?.[sectionIndex] || savedDaySections?.[sectionIndex];
    const touchKey = getDraftSectionTouchKey(section, sectionIndex);
    setDraftSectionTouched((prev) => ({
      ...prev,
      [touchKey]: {
        ...(prev[touchKey] || {}),
        [field]: value,
      },
    }));
  };
  const validateDraftSavedSections = () => {
    const nextTouched = {};
    let hasInvalidDraftSection = false;

    flatSections.forEach((section, sectionIndex) => {
      const isDraft = getSectionDetailId(section) === 0;
      if (!isDraft) return;
      const item = section?.item || section?.items?.[0] || {};
      const touchKey = getDraftSectionTouchKey(section, sectionIndex);
      const sectionTouched = {
        customerId: !section?.customerId,
        projectId: !section?.projectId,
        billingType: !item?.billingType,
      };

      if (
        sectionTouched.customerId ||
        sectionTouched.projectId ||
        sectionTouched.billingType
      ) {
        hasInvalidDraftSection = true;
      }

      nextTouched[touchKey] = {
        ...(draftSectionTouched[touchKey] || {}),
        ...sectionTouched,
      };
    });

    if (!hasInvalidDraftSection) {
      return true;
    }

    setDraftSectionTouched((prev) => ({
      ...prev,
      ...nextTouched,
    }));
    return false;
  };
  const validateAttachmentContext = () => {
    const statementId = Number(
      statementContext?.statement?.billingStatementId || 0,
    );
    const projectId = String(billingAttachmentDetail?.hprojectID || "").trim();

    if (!statementId) {
      showToast(
        "Please save the billing entry before viewing or uploading attachments.",
        "error",
      );
      return null;
    }

    if (!projectId) {
      showToast(
        "Please select a valid project before uploading attachments.",
        "error",
      );
      return null;
    }

    return { statementId, projectId };
  };

  // Avoid timezone shifts when the API sends YYYY-MM-DD or ISO strings.
  // Returns the app's date key format (same as `formatDate(selectedDate)`).
  const formatApiDateKey = (value, fallbackDateKey) => {
    if (!value) return fallbackDateKey;
    if (value instanceof Date) return formatDate(value);

    const raw = String(value);
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return formatDate(new Date(Number(year), Number(month) - 1, Number(day)));
    }

    return formatDate(raw);
  };

  const parsePeriodKeyRange = (periodKey) => {
    const raw = String(periodKey ?? "").replace(/\D/g, "");
    if (raw.length !== 16) return null;

    const startRaw = raw.slice(0, 8);
    const endRaw = raw.slice(8, 16);

    const toDateString = (chunk) => {
      const mm = Number(chunk.slice(0, 2));
      const dd = Number(chunk.slice(2, 4));
      const yyyy = Number(chunk.slice(4, 8));
      if (
        !Number.isFinite(mm) ||
        !Number.isFinite(dd) ||
        !Number.isFinite(yyyy) ||
        mm < 1 ||
        mm > 12 ||
        dd < 1 ||
        dd > 31
      ) {
        return null;
      }
      return `${String(mm).padStart(2, "0")}/${String(dd).padStart(2, "0")}/${yyyy}`;
    };

    const billingStart = toDateString(startRaw);
    const billingEnd = toDateString(endRaw);
    if (!billingStart || !billingEnd) return null;

    return { billingStart, billingEnd };
  };

  const parseUsDateStrict = (value) => {
    if (!value) return null;
    const match = String(value)
      .trim()
      .match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) return null;
    const [, mm, dd, yyyy] = match;
    const dateObj = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    if (Number.isNaN(dateObj.getTime())) return null;
    if (
      dateObj.getMonth() + 1 !== Number(mm) ||
      dateObj.getDate() !== Number(dd) ||
      dateObj.getFullYear() !== Number(yyyy)
    ) {
      return null;
    }
    return dateObj;
  };

  const formatUsDate = (dateObj) =>
    `${String(dateObj.getMonth() + 1).padStart(2, "0")}/${String(
      dateObj.getDate(),
    ).padStart(2, "0")}/${dateObj.getFullYear()}`;

  const sanitizeUsDateString = (value, fallback = "") => {
    const parsed = parseUsDateStrict(value);
    if (!parsed) return fallback;
    return formatUsDate(parsed);
  };

  const resolveStatementPeriod = (source, fallbackPeriod) => {
    const periodKey =
      source?.PeriodKey ??
      source?.periodKey ??
      source?.PeriodID ??
      source?.periodID ??
      "";
    const periodFromKey = parsePeriodKeyRange(periodKey);

    const sourceStart = sanitizeUsDateString(
      source?.BillingStart || source?.billingStart,
      "",
    );
    const sourceEnd = sanitizeUsDateString(
      source?.BillingEnd || source?.billingEnd,
      "",
    );
    const billingStart =
      sourceStart ||
      periodFromKey?.billingStart ||
      sanitizeUsDateString(fallbackPeriod?.billingStart, "") ||
      "";
    const billingEnd =
      sourceEnd ||
      periodFromKey?.billingEnd ||
      sanitizeUsDateString(fallbackPeriod?.billingEnd, "") ||
      "";

    return {
      billingStart,
      billingEnd,
      periodKey:
        periodKey ||
        (billingStart && billingEnd
          ? toPeriodKey(new Date(billingStart), new Date(billingEnd))
          : ""),
    };
  };

  const deepClone = (value) => {
    if (value == null) return value;
    return JSON.parse(JSON.stringify(value));
  };

  function explodeSectionsByItem(sections = []) {
    return (Array.isArray(sections) ? sections : []).flatMap(
      (section, sectionIndex) => {
        const sectionItems = Array.isArray(section?.items) ? section.items : [];
        if (sectionItems.length <= 1) return [section];

        return sectionItems.map((item, itemIndex) => ({
          ...section,
          items: [item],
          sectionKey:
            section?.sectionKey ||
            `${section?.projectId || "project"}-${section?.statement?.billingStatementId || 0}-${item?.billingStatementDetailsId || `new-${sectionIndex}-${itemIndex}-${section?.clientSectionKey || "client"}`}`,
        }));
      },
    );
  }

  function sortSectionsForDisplay(sections = []) {
    return [...(Array.isArray(sections) ? sections : [])].sort((a, b) => {
      const isDraftA = getSectionDetailId(a) === 0;
      const isDraftB = getSectionDetailId(b) === 0;

      if (isDraftA !== isDraftB) return isDraftA ? 1 : -1;
      if (isDraftA && isDraftB) {
        return Number(a?.createdAt || 0) - Number(b?.createdAt || 0);
      }

      const projectA = String(a?.projectId ?? "");
      const projectB = String(b?.projectId ?? "");
      if (projectA !== projectB) {
        const numericA = Number.parseInt(projectA, 10);
        const numericB = Number.parseInt(projectB, 10);
        if (Number.isFinite(numericA) && Number.isFinite(numericB)) {
          return numericA - numericB;
        }
        return projectA.localeCompare(projectB, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }

      const detailA = Number(
        a?.items?.[0]?.billingStatementDetailsId ??
          a?.billingStatementDetailsId ??
          0,
      );
      const detailB = Number(
        b?.items?.[0]?.billingStatementDetailsId ??
          b?.billingStatementDetailsId ??
          0,
      );

      const normalizedA =
        Number.isFinite(detailA) && detailA > 0
          ? detailA
          : Number.MAX_SAFE_INTEGER;
      const normalizedB =
        Number.isFinite(detailB) && detailB > 0
          ? detailB
          : Number.MAX_SAFE_INTEGER;
      return normalizedA - normalizedB;
    });
  }

  function getSectionDetailId(section) {
    return Number(
      section?.item?.billingStatementDetailsId ??
        section?.items?.[0]?.billingStatementDetailsId ??
        section?.billingStatementDetailsId ??
        0,
    );
  }

  const resolveDailyEntryRates = ({ entry, project }) => {
    const dayRate = parseNumber(
      entry?.DayRate ??
        entry?.dayRate ??
        entry?.BillingDayRate ??
        entry?.billingDayRate ??
        project?.BillingDayRate ??
        0,
    );

    const officePerDiem = parseNumber(
      entry?.PayableOfficePerDiem ??
        entry?.payableOfficePerDiem ??
        entry?.BillableOfficePerDiem ??
        entry?.billableOfficePerDiem ??
        entry?.OfficePerDiem ??
        entry?.officePerDiem ??
        entry?.OfficePerDiemRate ??
        entry?.officePerDiemRate ??
        project?.BillableOfficePerDiem ??
        project?.PayableOfficePerDiem ??
        project?.OfficePerDiem ??
        project?.OfficePerDiemRate ??
        0,
    );

    const travelPerDiem = parseNumber(
      entry?.PayableTravelPerDiem ??
        entry?.payableTravelPerDiem ??
        entry?.BillableTravelPerDiem ??
        entry?.billableTravelPerDiem ??
        entry?.TravelPerDiem ??
        entry?.travelPerDiem ??
        entry?.TravelPerDiemRate ??
        entry?.travelPerDiemRate ??
        project?.BillableTravelPerDiem ??
        project?.PayableTravelPerDiem ??
        project?.TravelPerDiem ??
        project?.TravelPerDiemRate ??
        0,
    );

    return { dayRate, officePerDiem, travelPerDiem };
  };

  const resolveApiBoolean = (value) => {
    if (value === true || value === false) return value;
    if (value == null) return false;
    if (typeof value === "number") return value !== 0;

    const normalized = String(value).trim().toLowerCase();
    if (normalized === "true" || normalized === "1" || normalized === "yes")
      return true;
    if (normalized === "false" || normalized === "0" || normalized === "no")
      return false;

    return Boolean(value);
  };

  const hydrateDetailRates = ({ detail, rates }) => {
    if (!detail) return detail;

    const resolvedDayRate = rates?.dayRate || parseNumber(detail?.DayRate) || 0;
    const rawPerDiemRate = parseNumber(detail?.PerDiemRate);
    const isOffice = resolveApiBoolean(detail?.IsOfficePerDiem);
    const isTravel = resolveApiBoolean(detail?.IsTravelPerDiem);
    const resolvedPerDiemRate =
      rawPerDiemRate ||
      (isOffice ? rates?.officePerDiem : 0) ||
      (isTravel ? rates?.travelPerDiem : 0) ||
      0;

    return {
      ...detail,
      DayRate: resolvedDayRate,
      PerDiemRate: resolvedPerDiemRate,
    };
  };

  const applyPerDiemSelectionFromFlags = (items, rates) => {
    const officeRate = formatRate(rates?.officePerDiem ?? 0);
    const travelRate = formatRate(rates?.travelPerDiem ?? 0);

    return (Array.isArray(items) ? items : []).map((item) => {
      const currentRate = formatRate(item?.perDiemRate);
      const isOfficeFlag = resolveApiBoolean(item?.isOfficePerDiem);
      const isTravelFlag = resolveApiBoolean(item?.isTravelPerDiem);

      const shouldResolveFromFlags =
        (!currentRate || currentRate === "0.00" || currentRate === "NaN") &&
        (isOfficeFlag || isTravelFlag);

      const nextPerDiemRate = shouldResolveFromFlags
        ? isOfficeFlag
          ? officeRate
          : travelRate
        : currentRate;

      let nextIsOffice = isOfficeFlag;
      let nextIsTravel = isTravelFlag;

      if (nextPerDiemRate === "0.00") {
        nextIsOffice = false;
        nextIsTravel = false;
      } else if (!nextIsOffice && !nextIsTravel) {
        const matchesOffice = nextPerDiemRate === officeRate;
        const matchesTravel = nextPerDiemRate === travelRate;

        if (matchesOffice && matchesTravel) {
          nextIsOffice = true;
        } else if (matchesOffice) {
          nextIsOffice = true;
        } else if (matchesTravel) {
          nextIsTravel = true;
        }
      }

      return {
        ...item,
        perDiemRate: nextPerDiemRate,
        isOfficePerDiem: nextIsOffice,
        isTravelPerDiem: nextIsTravel,
        perDiem: calculatePerDiem(item?.hours, nextPerDiemRate),
      };
    });
  };

  const resolveProjectRatesFromProjectByIdResponse = (response) => {
    const loggedInUserId = getLogUserId();
    const broker = response?.BrokerDetails?.find(
      (item) => String(item?.UserID) === String(loggedInUserId),
    );
    const project = response?.ProjectDetails?.[0];

    const dayRate = parseNumber(
      broker?.DayRate ??
        broker?.BillingDayRate ??
        project?.BillingDayRate ??
        project?.billingDayRate ??
        project?.DayRate ??
        project?.dayRate ??
        0,
    );
    const officePerDiem = parseNumber(
      broker?.PayableOfficePerDiem ??
        broker?.payableOfficePerDiem ??
        broker?.OfficePerDiem ??
        broker?.officePerDiem ??
        broker?.OfficePerDiemRate ??
        broker?.officePerDiemRate ??
        project?.BillableOfficePerDiem ??
        project?.billableOfficePerDiem ??
        project?.PayableOfficePerDiem ??
        project?.payableOfficePerDiem ??
        project?.OfficePerDiem ??
        project?.officePerDiem ??
        project?.OfficePerDiemRate ??
        project?.officePerDiemRate ??
        0,
    );
    const travelPerDiem = parseNumber(
      broker?.PayableTravelPerDiem ??
        broker?.payableTravelPerDiem ??
        broker?.TravelPerDiem ??
        broker?.travelPerDiem ??
        broker?.TravelPerDiemRate ??
        broker?.travelPerDiemRate ??
        project?.BillableTravelPerDiem ??
        project?.billableTravelPerDiem ??
        project?.PayableTravelPerDiem ??
        project?.payableTravelPerDiem ??
        project?.TravelPerDiem ??
        project?.travelPerDiem ??
        project?.TravelPerDiemRate ??
        project?.travelPerDiemRate ??
        0,
    );
    const mileageRate = parseNumber(
      project?.MileageRate ?? project?.mileageRate ?? 0,
    );

    return { dayRate, officePerDiem, travelPerDiem, mileageRate };
  };

  const isNoRecordFoundResponse = (source) => {
    if (!source) return false;
    if (typeof source === "string") {
      return source.toLowerCase().includes("no record found");
    }
    const message =
      source?.message?.message ??
      source?.message ??
      source?.Message ??
      source?.error ??
      source?.Error ??
      "";
    return String(message).toLowerCase().includes("no record found");
  };

  const validateProjectSelectionForDate = async ({
    projectId,
    customerName,
    projectName,
  }) => {
    if (!projectId) {
      return { allowed: true, existingStatement: null };
    }

    const billingDateLabel = formatDisplayDate(selectedDate);

    try {
      const response = await checkStatementExistsForDate({
        projectId,
        date: formatDate(selectedDate),
      });

      if (
        isNoRecordFoundResponse(response) ||
        (Array.isArray(response?.data) && response.data.length === 0)
      ) {
        return { allowed: true, existingStatement: null };
      }

      const statusSource = Array.isArray(response?.data)
        ? response.data[0]
        : response;
      const statusId = resolveStatusId(statusSource);

      if (statusId && statusId !== 1) {
        showToast(
          `The billing statement for ${customerName || "this customer"} - ${projectName || "this project"} on ${billingDateLabel} is already closed or invoiced. Please select a different project.`,
          "error",
        );
        return { allowed: false, existingStatement: null };
      }

      return { allowed: true, existingStatement: statusSource || null };
    } catch (error) {
      const apiError = parseBillingApiError(error);
      if (
        isNoRecordFoundResponse(apiError) ||
        isNoRecordFoundResponse(apiError?.message)
      ) {
        return { allowed: true, existingStatement: null };
      }

      showToast(
        `Unable to validate statement status for ${customerName || "this customer"} - ${projectName || "this project"} on ${billingDateLabel}. Please try again.`,
        "error",
      );
      return { allowed: false, existingStatement: null };
    }
  };

  const parseBillingDayResponse = (dayDetails, fallbackDateKey) =>
    (Array.isArray(dayDetails) ? dayDetails : []).map((entry) => {
      let parsedDetails = [];
      const statusId = resolveStatusId(entry);
      const billingStatementId = Number.parseInt(
        entry?.billingStatementId ??
          entry?.BillingStatementId ??
          entry?.BillingStatementID ??
          entry?.HeaderID ??
          0,
        10,
      );

      if (typeof entry?.billingDetails === "string" && entry.billingDetails) {
        try {
          parsedDetails = JSON.parse(entry.billingDetails);
        } catch (error) {
          parsedDetails = [];
        }
      } else if (Array.isArray(entry?.billingDetails)) {
        parsedDetails = entry.billingDetails;
      }

      const filteredDetails = (
        Array.isArray(parsedDetails) ? parsedDetails : []
      ).filter(
        (detail) =>
          formatApiDateKey(detail?.Date, fallbackDateKey) === fallbackDateKey,
      );

      return {
        ...entry,
        StatusID: statusId,
        billingStatementId: Number.isFinite(billingStatementId)
          ? billingStatementId
          : 0,
        parsedDetails: filteredDetails.map((detail) => ({
          // Normalize the new/legacy flag fields so downstream mapping is consistent.
          // API variants observed: IsOfficePerDiem / isOfficePerDiem / sOfficePerDiem, etc.
          billingStatementDetailsId:
            detail?.billingStatementDetailsId ??
            detail?.BillingStatementDetailsId ??
            0,
          HeaderID:
            detail?.HeaderID ??
            detail?.headerId ??
            entry?.billingStatementId ??
            0,
          BillingType:
            detail?.BillingType ??
            detail?.billingType ??
            detail?.BillingTypeId ??
            detail?.billingTypeId ??
            "",
          Date: formatApiDateKey(detail?.Date, fallbackDateKey),
          Hours: detail?.Hours ?? detail?.hours ?? 0,
          Miles: detail?.Miles ?? detail?.miles ?? 0,
          Lodging: detail?.Lodging ?? detail?.lodging ?? 0,
          Copies: detail?.Copies ?? detail?.copies ?? 0,
          PerDiemRate: detail?.PerDiemRate ?? detail?.perDiemRate ?? 0,
          PerDiem: detail?.PerDiem ?? detail?.perDiem ?? 0,
          Misc: detail?.Misc ?? detail?.misc ?? 0,
          DayRate: detail?.DayRate ?? detail?.dayRate ?? 0,
          Description: detail?.Description ?? detail?.description ?? "",
          IsOfficePerDiem:
            detail?.IsOfficePerDiem ??
            detail?.isOfficePerDiem ??
            detail?.sOfficePerDiem ??
            detail?.sOfficePerdiem ??
            false,
          IsTravelPerDiem:
            detail?.IsTravelPerDiem ??
            detail?.isTravelPerDiem ??
            detail?.isTravelPerdiem ??
            detail?.sTravelPerDiem ??
            detail?.sTravelPerdiem ??
            false,
        })),
      };
    });

  const buildSavedDaySections = async (date, dayEntries, lookupData = {}) => {
    const customersList = Array.isArray(lookupData?.customers)
      ? lookupData.customers
      : customers;
    const assignedProjectsList = Array.isArray(lookupData?.assignedProjects)
      ? lookupData.assignedProjects
      : assignedProjects;
    const projectCache = {};
    const sectionGroups = await Promise.all(
      dayEntries.map(async (entry) => {
        const projectId =
          entry?.ProjectId ??
          entry?.projectId ??
          entry?.ProjID ??
          entry?.projID ??
          entry?.HeaderProjectId ??
          entry?.headerProjectId ??
          "";
        const assignedProject = assignedProjectsList.find(
          (project) => String(project.ProjectID) === String(projectId),
        );
        const projectDetailsResponse =
          !assignedProject && projectId
            ? await getProjectDetailsCached(projectId)
            : null;
        const projectDetails =
          projectDetailsResponse?.ProjectDetails?.[0] || null;
        const customerId = assignedProject?.CustomerID
          ? String(assignedProject.CustomerID)
          : projectDetails?.CustomerId
            ? String(projectDetails.CustomerId)
            : "";
        const customer =
          customersList.find(
            (item) => String(item.CustomerID) === String(customerId),
          ) || null;

        if (customerId && !projectCache[customerId]) {
          projectCache[customerId] = await fetchProjectsForCustomer(
            customerId,
            assignedProjectsList,
          );
        }

        const customerProjects = projectCache[customerId] || [];
        const project =
          customerProjects.find(
            (item) => String(item.ProjectId) === String(projectId),
          ) || null;
        const period = resolveActivePeriod(date, project?.BillingPeriod);
        const projectDayRate = parseNumber(project?.BillingDayRate);
        const entryRates = resolveDailyEntryRates({ entry, project });
        const items = (entry?.parsedDetails || []).map((detail) =>
          mapApiDetailToItem({
            ...detail,
            // DayRate is used by the UI and client-side total calc.
            // If the detail doesn't include it, fall back to the saved header rate, then the project filter.
            DayRate:
              parseNumber(detail?.DayRate) ||
              entryRates.dayRate ||
              projectDayRate ||
              0,
            BillingDayRate: projectDayRate || 0,
            PerDiemRate:
              parseNumber(detail?.PerDiemRate) ||
              (resolveApiBoolean(detail?.IsOfficePerDiem)
                ? entryRates.officePerDiem
                : 0) ||
              (resolveApiBoolean(detail?.IsTravelPerDiem)
                ? entryRates.travelPerDiem
                : 0) ||
              0,
          }),
        );
        const resolvedPeriod = resolveStatementPeriod(entry, period);
        const baseSection = {
          customerId,
          customerName:
            customer?.CustomerName || projectDetails?.CustomerName || "",
          projectId: String(projectId),
          projectName:
            project?.Name ||
            assignedProject?.ProjectName ||
            projectDetails?.Name ||
            projectDetails?.ProjectName ||
            "",
          rates: entryRates,
          perDiemRates: {
            office: formatRate(entryRates.officePerDiem),
            travel: formatRate(entryRates.travelPerDiem),
          },
          statement: {
            billingStatementId: entry?.billingStatementId || 0,
            BillingStart: resolvedPeriod.billingStart,
            BillingEnd: resolvedPeriod.billingEnd,
            PeriodKey: resolvedPeriod.periodKey,
            DayRate: parseNumber(entry?.DayRate ?? entry?.dayRate ?? 0),
            StatusID: resolveStatusId(entry),
          },
          periodLabel: `${resolvedPeriod.billingStart} - ${resolvedPeriod.billingEnd}`,
        };

        // Render each billing detail as its own section card.
        return (items || []).map((item) => ({
          ...baseSection,
          items: [item],
          sectionKey: `${baseSection.projectId}-${baseSection.statement.billingStatementId}-${item?.billingStatementDetailsId || "new"}`,
        }));
      }),
    );
    return sectionGroups.flat();
  };

  const hydratePopup = async (date, customerId, projectId, lookupData = {}) => {
    const customersList = Array.isArray(lookupData?.customers)
      ? lookupData.customers
      : customers;
    const assignedProjectsList = Array.isArray(lookupData?.assignedProjects)
      ? lookupData.assignedProjects
      : assignedProjects;
    const dateKey = formatDate(date);
    setDraftSectionTouched({});
    setShowManualSection(false);
    setProjectRateOverride(null);
    const dayResponse = await fetchDailyEntryBillingByDay({ date: dateKey });
    const dayDetails = parseBillingDayResponse(dayResponse?.data, dateKey);
    setPopupSummary({
      hours: parseNumber(dayResponse?.totalHours),
      amount: parseNumber(dayResponse?.billingTotal),
    });

    if (dayDetails.length > 0) {
      const projectRateCache = {};
      const loadProjectRates = async (projectId) => {
        if (!projectId) return null;
        const key = String(projectId);
        if (projectRateCache[key]) return projectRateCache[key];

        try {
          const response = await getProjectDetailsCached(projectId, {
            forceRefresh: true,
          });
          const rates = resolveProjectRatesFromProjectByIdResponse(response);
          projectRateCache[key] = rates;
          return rates;
        } catch (error) {
          return null;
        }
      };

      const sections = sortSectionsForDisplay(
        explodeSectionsByItem(
          await buildSavedDaySections(date, dayDetails, {
            customers: customersList,
            assignedProjects: assignedProjectsList,
          }),
        ),
      );

      const enrichedSections = await Promise.all(
        sections.map(async (section) => {
          if (Number(section?.statement?.billingStatementId || 0) === 0) {
            return section;
          }

          const rates = await loadProjectRates(section.projectId);
          if (!rates) return section;

          return {
            ...section,
            items: applyPerDiemSelectionFromFlags(section.items, rates),
            displayRates: rates,
            perDiemRates: {
              office: formatRate(rates.officePerDiem),
              travel: formatRate(rates.travelPerDiem),
            },
          };
        }),
      );

      const sortedSavedSections = sortSectionsForDisplay(
        explodeSectionsByItem(enrichedSections),
      );
      const nextInitialRates = {};
      (sortedSavedSections || []).forEach((section) => {
        const projectKey = String(section?.projectId || "");
        if (!projectKey || nextInitialRates[projectKey]) return;
        const item = section?.items?.[0] || {};
        nextInitialRates[projectKey] = {
          dayRate: normalizeRateNumber(
            section?.displayRates?.dayRate ??
              section?.rates?.dayRate ??
              item?.dayRate ??
              0,
          ),
          mileageRate: normalizeRateNumber(
            section?.displayRates?.mileageRate ??
              section?.rates?.mileageRate ??
              item?.mileageRate ??
              0,
          ),
          officePerDiem: normalizeRateNumber(
            section?.displayRates?.officePerDiem ??
              section?.perDiemRates?.office ??
              0,
          ),
          travelPerDiem: normalizeRateNumber(
            section?.displayRates?.travelPerDiem ??
              section?.perDiemRates?.travel ??
              0,
          ),
        };
      });
      setInitialRatesByProject(nextInitialRates);
      setSavedDaySections(sortedSavedSections);
      const popupCustomerId =
        String(sortedSavedSections?.[0]?.customerId || "") ||
        String(customerId || "") ||
        (customersList[0] ? String(customersList[0].CustomerID) : "");
      const popupProjects = popupCustomerId
        ? await fetchProjectsForCustomer(popupCustomerId, assignedProjectsList)
        : [];
      const nextSeed = getDefaultForm(date);
      const nextStatementContext = null;

      setAvailableProjects(popupProjects);
      setStatementContext(null);
      setProjectRateOverride(null);
      setFormSeed(nextSeed);
      formik.resetForm({ values: deepClone(nextSeed) });
      setShowManualSection(false);
      setOriginalPopupState({
        dateKey,
        formSeed: deepClone(nextSeed),
        popupSummary: {
          hours: parseNumber(dayResponse?.totalHours),
          amount: parseNumber(dayResponse?.billingTotal),
        },
        statementContext: deepClone(nextStatementContext),
        availableProjects: deepClone(popupProjects),
        dayDetails: deepClone(dayDetails),
        savedDaySections: deepClone(sortedSavedSections),
        showManualSection: false,
      });
      return;
    }

    setSavedDaySections([]);
    const matchedSavedEntry =
      dayDetails.find((detail) => {
        const detailProjectId =
          detail?.ProjectId ??
          detail?.projectId ??
          detail?.ProjID ??
          detail?.projID ??
          detail?.HeaderProjectId ??
          detail?.headerProjectId;

        return projectId && String(detailProjectId) === String(projectId);
      }) || dayDetails[0];
    const savedProjectId =
      matchedSavedEntry?.ProjectId ??
      matchedSavedEntry?.projectId ??
      matchedSavedEntry?.ProjID ??
      matchedSavedEntry?.projID ??
      matchedSavedEntry?.HeaderProjectId ??
      matchedSavedEntry?.headerProjectId;
    const savedCustomerId =
      savedProjectId != null
        ? assignedProjectsList.find(
            (project) => String(project.ProjectID) === String(savedProjectId),
          )?.CustomerID
        : null;
    const resolvedCustomerId =
      savedCustomerId != null
        ? String(savedCustomerId)
        : customerId ||
          (customersList[0] ? String(customersList[0].CustomerID) : "");
    const projects = resolvedCustomerId
      ? await fetchProjectsForCustomer(resolvedCustomerId, assignedProjectsList)
      : [];

    setAvailableProjects(projects);

    const resolvedProjectId =
      savedProjectId != null
        ? String(savedProjectId)
        : projectId || (projects[0] ? String(projects[0].ProjectId) : "");
    const selectedProject = projects.find(
      (project) => String(project.ProjectId) === String(resolvedProjectId),
    );
    const period = resolveActivePeriod(
      date,
      selectedProject?.BillingPeriod,
    );
    const matchedEntryPeriod = resolveStatementPeriod(
      matchedSavedEntry,
      period,
    );
    const statement = matchedSavedEntry?.billingStatementId
      ? {
          billingStatementId: matchedSavedEntry.billingStatementId,
          BillingStart: matchedEntryPeriod.billingStart,
          BillingEnd: matchedEntryPeriod.billingEnd,
          PeriodKey: matchedEntryPeriod.periodKey,
          DayRate: parseNumber(
            matchedSavedEntry?.DayRate ?? matchedSavedEntry?.dayRate ?? 0,
          ),
          StatusID: resolveStatusId(matchedSavedEntry),
        }
      : resolvedProjectId
        ? await findBillingStatement({
            projectId: resolvedProjectId,
            billingStart: period.billingStart,
            billingEnd: period.billingEnd,
          })
        : null;
    const normalizeStatement = (rawStatement) => {
      if (!rawStatement) return null;
      const resolvedPeriod = resolveStatementPeriod(rawStatement, period);
      const idSource =
        rawStatement?.billingStatementId ??
        rawStatement?.BillingStatementId ??
        rawStatement?.BillingStatementID ??
        rawStatement?.HeaderID ??
        rawStatement?.headerId ??
        rawStatement?.ID ??
        rawStatement?.Id ??
        rawStatement?.id ??
        0;
      const parsedId = Number.parseInt(idSource || 0, 10);
      const billingStatementId = Number.isFinite(parsedId) ? parsedId : 0;
      return {
        ...rawStatement,
        billingStatementId,
        BillingStart: resolvedPeriod.billingStart,
        BillingEnd: resolvedPeriod.billingEnd,
        PeriodKey: resolvedPeriod.periodKey,
        StatusID: resolveStatusId(rawStatement) || 1,
      };
    };
    const normalizedStatement = normalizeStatement(statement);
    const projectDayRate = parseNumber(selectedProject?.BillingDayRate);
    const savedRates = resolveDailyEntryRates({
      entry: matchedSavedEntry,
      project: selectedProject,
    });
    let effectiveRates = savedRates;

    if (resolvedProjectId) {
      try {
        const response = await getProjectDetailsCached(resolvedProjectId, {
          forceRefresh: true,
        });
        const rates = resolveProjectRatesFromProjectByIdResponse(response);
        effectiveRates = rates;
        setProjectRateOverride({
          projectId: resolvedProjectId,
          dayRate: rates.dayRate,
          officePerDiem: rates.officePerDiem,
          travelPerDiem: rates.travelPerDiem,
        });
      } catch (error) {
        setProjectRateOverride({
          projectId: resolvedProjectId,
          dayRate: savedRates.dayRate,
          officePerDiem: savedRates.officePerDiem,
          travelPerDiem: savedRates.travelPerDiem,
        });
      }
    }

    const selectedItems = matchedSavedEntry?.parsedDetails?.length
      ? matchedSavedEntry.parsedDetails
          .map((detail) =>
            hydrateDetailRates({ detail, rates: effectiveRates }),
          )
          .map(mapApiDetailToItem)
      : [];
    const enrichedSelectedItems = applyPerDiemSelectionFromFlags(
      selectedItems,
      effectiveRates,
    ).map((item) => {
      const dayRate = parseNumber(item.dayRate);
      const nextDayRate = dayRate
        ? item.dayRate
        : savedRates.dayRate
          ? savedRates.dayRate.toFixed(2)
          : projectDayRate
            ? projectDayRate.toFixed(2)
            : "0.00";
      return { ...item, dayRate: nextDayRate };
    });
    const items =
      enrichedSelectedItems.length > 0
        ? enrichedSelectedItems
        : [createBlankLineItem(dateKey)];

    setStatementContext({
      statement: normalizedStatement,
      projectId: resolvedProjectId,
      customerId: resolvedCustomerId,
      periodLabel: `${normalizedStatement?.BillingStart || period.billingStart} - ${normalizedStatement?.BillingEnd || period.billingEnd}`,
      items,
    });
    const nextSeed = {
      customerId: resolvedCustomerId,
      projectId: resolvedProjectId,
      items,
    };
    setInitialRatesByProject({
      [String(resolvedProjectId || "")]: {
        dayRate: normalizeRateNumber(
          projectRateOverride?.dayRate ??
            effectiveRates?.dayRate ??
            selectedProject?.BillingDayRate ??
            0,
        ),
        mileageRate: normalizeRateNumber(
          effectiveRates?.mileageRate ??
            selectedProject?.MileageRate ??
            selectedProject?.mileageRate ??
            0,
        ),
        officePerDiem: normalizeRateNumber(
          projectRateOverride?.officePerDiem ??
            effectiveRates?.officePerDiem ??
            0,
        ),
        travelPerDiem: normalizeRateNumber(
          projectRateOverride?.travelPerDiem ??
            effectiveRates?.travelPerDiem ??
            0,
        ),
      },
    });
    setFormSeed(nextSeed);
    formik.resetForm({ values: deepClone(nextSeed) });
    setShowManualSection(true);
    setOriginalPopupState({
      dateKey,
      formSeed: deepClone(nextSeed),
      popupSummary: {
        hours: parseNumber(dayResponse?.totalHours),
        amount: parseNumber(dayResponse?.billingTotal),
      },
      statementContext: deepClone({
        statement: normalizedStatement,
        periodLabel: `${normalizedStatement?.BillingStart || period.billingStart} - ${normalizedStatement?.BillingEnd || period.billingEnd}`,
        items,
      }),
      availableProjects: deepClone(projects),
      dayDetails: deepClone(dayDetails),
      savedDaySections: [],
      showManualSection: true,
    });
  };

  const openBillingPopup = async (date) => {
    const dateKey = formatDate(date);
    let customerId =
      formik.values.customerId ||
      formSeed.customerId ||
      (customers[0] ? String(customers[0].CustomerID) : "");
    const projectId = formik.values.projectId || formSeed.projectId;

    setSelectedDate(date);
    setCurrentMonthDate(new Date(date.getFullYear(), date.getMonth(), 1));
    setIsBillingOpen(true);
    setShowManualSection(false);
    setInitialRatesByProject({});
    manualRateOverrideRef.current = { dayRate: false, mileageRate: false };
    setPopupLoading(true);

    try {
      const assignmentsResponse = await fetchAssignedBillingProjects();
      setCustomers(assignmentsResponse.customers);
      setAssignedProjects(assignmentsResponse.assignedProjects);

      if (!customerId && assignmentsResponse.customers.length > 0) {
        customerId = String(assignmentsResponse.customers[0].CustomerID);
      }

      if (customerId) {
        const popupProjects = await fetchProjectsForCustomer(
          customerId,
          assignmentsResponse.assignedProjects,
        );
        setAvailableProjects(popupProjects);
      }

      await hydratePopup(date, customerId, projectId, {
        customers: assignmentsResponse.customers,
        assignedProjects: assignmentsResponse.assignedProjects,
      });
    } catch (error) {
      const apiError = parseBillingApiError(error);
      // showToast(
      //   apiError?.message?.message ||
      //     apiError?.message ||
      //     "Unable to load billing entry.",
      //   "error"
      // );
      setStatementContext(null);
      setSavedDaySections([]);
      setPopupSummary({ hours: 0, amount: 0 });
      const fallbackSeed = getDefaultForm(date);
      setFormSeed(fallbackSeed);
      formik.resetForm({ values: deepClone(fallbackSeed) });
      setOriginalPopupState({
        dateKey,
        formSeed: deepClone(fallbackSeed),
        popupSummary: { hours: 0, amount: 0 },
        statementContext: null,
        availableProjects: [],
        dayDetails: [],
        savedDaySections: [],
        showManualSection: false,
      });
    } finally {
      setPopupLoading(false);
    }
  };

  const handleBillingCancel = async () => {
    if (isDeleting) return;
    manualRateOverrideRef.current = { dayRate: false, mileageRate: false };
    setDraftSectionTouched({});
    const dateKey = formatDate(selectedDate);
    if (originalPopupState?.dateKey === dateKey) {
      const restoredSeed = deepClone(
        originalPopupState.formSeed || getDefaultForm(selectedDate),
      );
      setSavedDaySections(
        sortSectionsForDisplay(
          deepClone(originalPopupState.savedDaySections || []),
        ),
      );
      setStatementContext(deepClone(originalPopupState.statementContext));
      setAvailableProjects(
        deepClone(originalPopupState.availableProjects || []),
      );
      setPopupSummary(
        deepClone(originalPopupState.popupSummary || { hours: 0, amount: 0 }),
      );
      setFormSeed(restoredSeed);
      setShowManualSection(Boolean(originalPopupState.showManualSection));
      // Force Formik to drop any unsaved edits immediately.
      formik.resetForm({ values: restoredSeed });
    }
    await refreshCalendarEntries({
      anchorDate: selectedDate,
      mode: viewMode,
    });
    setIsBillingOpen(false);
  };

  const handleCustomerChange = async (event) => {
    const customerId = event.target.value;
    formik.setFieldValue("customerId", customerId);
    formik.setFieldTouched("customerId", true, false);
    setProjectRateOverride(null);
    setStatementContext(null);

    try {
      const projects = await fetchProjectsForCustomer(
        customerId,
        assignedProjects,
      );
      setAvailableProjects(projects);

      // New/Add More flow: don't refresh the popup. Just update dependent dropdowns locally.
      formik.setFieldValue("projectId", "");
      formik.setFieldValue(
        "items",
        (formik.values.items || []).map((item) => ({
          ...item,
          dayRate: "0.00",
          perDiemRate: "0.00",
          perDiem: "0.00",
          isOfficePerDiem: false,
          isTravelPerDiem: false,
        })),
      );
    } catch (error) {
      const apiError = parseBillingApiError(error);
      showToast(
        apiError?.message?.message ||
          apiError?.message ||
          "Unable to load projects.",
        "error",
      );
    } finally {
      // no popup refresh here
    }
  };

  const handleProjectChange = async (event) => {
    const projectId = String(
      event?.target?.value ??
        event?.target?.ProjectId ??
        event?.target?.ProjectID ??
        "",
    );
    setProjectRateOverride(null);
    let existingStatement = null;

    if (projectId) {
      formik.setFieldError("projectId", undefined);
      formik.setFieldTouched("projectId", false, false);
    } else {
      formik.setFieldTouched("projectId", true, false);
    }

    if (projectId) {
      const selectedProject = availableProjects.find(
        (project) =>
          String(project.ProjectId ?? project.ProjectID) === String(projectId),
      );
      const validation = await validateProjectSelectionForDate({
        projectId,
        customerName: selectedCustomer?.CustomerName || "this customer",
        projectName:
          selectedProject?.Name ||
          selectedProject?.ProjectName ||
          "this project",
      });

      if (!validation.allowed) {
        formik.setFieldValue("projectId", "");
        formik.setFieldValue(
          "items",
          (formik.values.items || []).map((item) => ({
            ...item,
            dayRate: "0.00",
          })),
        );
        return;
      }

      existingStatement = validation.existingStatement;
    }

    formik.setFieldValue("projectId", projectId);
    formik.setFieldError("projectId", undefined);
    formik.setFieldTouched("projectId", false, false);

    const selectedProject = availableProjects.find(
      (project) =>
        String(project.ProjectId ?? project.ProjectID) === String(projectId),
    );

    let resolvedDayRate = parseNumber(selectedProject?.BillingDayRate);
    let resolvedMileageRate = parseNumber(
      selectedProject?.MileageRate ?? selectedProject?.mileageRate ?? 0,
    );
    let resolvedOfficePerDiem = parseNumber(
      selectedProject?.BillableOfficePerDiem ??
        selectedProject?.PayableOfficePerDiem ??
        selectedProject?.OfficePerDiem ??
        selectedProject?.OfficePerDiemRate ??
        0,
    );
    let resolvedTravelPerDiem = parseNumber(
      selectedProject?.BillableTravelPerDiem ??
        selectedProject?.PayableTravelPerDiem ??
        selectedProject?.TravelPerDiem ??
        selectedProject?.TravelPerDiemRate ??
        0,
    );

    if (projectId) {
      try {
        const response = await getProjectDetailsCached(projectId);
        const loggedInUserId = getLogUserId();
        const broker = response?.BrokerDetails?.find(
          (item) => String(item?.UserID) === String(loggedInUserId),
        );
        const project = response?.ProjectDetails?.[0];

        resolvedDayRate = parseNumber(
          broker?.DayRate ?? project?.BillingDayRate ?? resolvedDayRate ?? 0,
        );
        resolvedMileageRate = parseNumber(
          project?.MileageRate ??
            project?.mileageRate ??
            resolvedMileageRate ??
            0,
        );
        resolvedOfficePerDiem = parseNumber(
          broker?.PayableOfficePerDiem ??
            project?.BillableOfficePerDiem ??
            resolvedOfficePerDiem ??
            0,
        );
        resolvedTravelPerDiem = parseNumber(
          broker?.PayableTravelPerDiem ??
            project?.BillableTravelPerDiem ??
            resolvedTravelPerDiem ??
            0,
        );

        setProjectRateOverride({
          projectId,
          dayRate: resolvedDayRate,
          officePerDiem: resolvedOfficePerDiem,
          travelPerDiem: resolvedTravelPerDiem,
        });
      } catch (error) {
        // Fall back to the already-loaded project filter data.
      }
    }

    const nextDayRate = resolvedDayRate ? resolvedDayRate.toFixed(2) : "0.00";
    const officeRate = formatRate(resolvedOfficePerDiem);
    const travelRate = formatRate(resolvedTravelPerDiem);
    const defaultPerDiemRate = "0.00";
    setInitialRatesByProject((prev) => ({
      ...(prev || {}),
      [String(projectId || "")]: {
        dayRate: normalizeRateNumber(nextDayRate),
        mileageRate: normalizeRateNumber(resolvedMileageRate),
        officePerDiem: normalizeRateNumber(resolvedOfficePerDiem),
        travelPerDiem: normalizeRateNumber(resolvedTravelPerDiem),
      },
    }));

    if (existingStatement) {
      const period = resolveActivePeriod(
        selectedDate,
        selectedProject?.BillingPeriod,
      );
      const resolvedPeriod = resolveStatementPeriod(existingStatement, period);
      const statementIdSource =
        existingStatement?.billingStatementId ??
        existingStatement?.BillingStatementId ??
        existingStatement?.BillingStatementID ??
        existingStatement?.HeaderID ??
        existingStatement?.headerId ??
        existingStatement?.ID ??
        existingStatement?.Id ??
        existingStatement?.id ??
        0;
      const parsedStatementId = Number.parseInt(statementIdSource || 0, 10);
      const normalizedStatementId = Number.isFinite(parsedStatementId)
        ? parsedStatementId
        : 0;

      let parsedDetails = [];
      if (
        typeof existingStatement?.billingDetails === "string" &&
        existingStatement.billingDetails
      ) {
        try {
          parsedDetails = JSON.parse(existingStatement.billingDetails);
        } catch (error) {
          parsedDetails = [];
        }
      } else if (Array.isArray(existingStatement?.billingDetails)) {
        parsedDetails = existingStatement.billingDetails;
      }

      const selectedDateKey = formatDate(selectedDate);
      const mappedSelectedItems = (
        Array.isArray(parsedDetails) ? parsedDetails : []
      )
        .filter(
          (detail) =>
            formatApiDateKey(detail?.Date ?? detail?.date, selectedDateKey) ===
            selectedDateKey,
        )
        .map((detail) =>
          mapApiDetailToItem({
            ...detail,
            billingStatementDetailsId:
              detail?.billingStatementDetailsId ??
              detail?.BillingStatementDetailsId ??
              0,
            HeaderID:
              detail?.HeaderID ??
              detail?.headerId ??
              normalizedStatementId ??
              0,
            BillingType:
              detail?.BillingType ??
              detail?.billingType ??
              detail?.BillingTypeId ??
              detail?.billingTypeId ??
              "",
            Date: formatApiDateKey(
              detail?.Date ?? detail?.date,
              selectedDateKey,
            ),
            Hours: detail?.Hours ?? detail?.hours ?? 0,
            Miles: detail?.Miles ?? detail?.miles ?? 0,
            Lodging: detail?.Lodging ?? detail?.lodging ?? 0,
            Copies: detail?.Copies ?? detail?.copies ?? 0,
            PerDiemRate: detail?.PerDiemRate ?? detail?.perDiemRate ?? 0,
            PerDiem: detail?.PerDiem ?? detail?.perDiem ?? 0,
            Misc: detail?.Misc ?? detail?.misc ?? 0,
            DayRate: detail?.DayRate ?? detail?.dayRate ?? nextDayRate,
            Description: detail?.Description ?? detail?.description ?? "",
            IsOfficePerDiem:
              detail?.IsOfficePerDiem ??
              detail?.isOfficePerDiem ??
              detail?.sOfficePerDiem ??
              detail?.sOfficePerdiem ??
              false,
            IsTravelPerDiem:
              detail?.IsTravelPerDiem ??
              detail?.isTravelPerDiem ??
              detail?.sTravelPerDiem ??
              detail?.sTravelPerdiem ??
              false,
          }),
        );

      const seededItemsRaw =
        mappedSelectedItems.length > 0
          ? mappedSelectedItems
          : [createBlankLineItem(selectedDateKey)];
      const seededItems = applyPerDiemSelectionFromFlags(seededItemsRaw, {
        officePerDiem: resolvedOfficePerDiem,
        travelPerDiem: resolvedTravelPerDiem,
      }).map((item) => ({
        ...item,
        dayRate: nextDayRate,
        perDiemRate: item?.perDiemRate
          ? formatRate(item.perDiemRate)
          : defaultPerDiemRate,
        perDiem: calculatePerDiem(
          item?.hours ?? "0",
          item?.perDiemRate ? formatRate(item.perDiemRate) : defaultPerDiemRate,
        ),
      }));

      setStatementContext({
        statement: {
          ...existingStatement,
          billingStatementId: normalizedStatementId,
          BillingStart: resolvedPeriod.billingStart,
          BillingEnd: resolvedPeriod.billingEnd,
          PeriodKey: resolvedPeriod.periodKey,
          StatusID: resolveStatusId(existingStatement) || 1,
        },
        projectId,
        customerId: formik.values.customerId,
        periodLabel: `${resolvedPeriod.billingStart} - ${resolvedPeriod.billingEnd}`,
        items: seededItems,
      });
      formik.setFieldValue("items", seededItems);
      return;
    }

    const latestItems = Array.isArray(latestFormikValuesRef.current?.items)
      ? latestFormikValuesRef.current.items
      : [];
    const nextItems = latestItems.map((item) => {
      const currentRate = formatRate(item?.perDiemRate);
      const shouldAutofill =
        !currentRate || currentRate === "0.00" || currentRate === "NaN";
      const perDiemRate = shouldAutofill ? defaultPerDiemRate : currentRate;
      const isOfficeSelected = resolveApiBoolean(item?.isOfficePerDiem);
      const isTravelSelected = resolveApiBoolean(item?.isTravelPerDiem);
      return {
        ...item,
        dayRate: nextDayRate,
        perDiemRate,
        isOfficePerDiem:
          perDiemRate === "0.00"
            ? false
            : isOfficeSelected ||
              (!isTravelSelected && perDiemRate === officeRate),
        isTravelPerDiem:
          perDiemRate === "0.00"
            ? false
            : isTravelSelected ||
              (!isOfficeSelected &&
                perDiemRate === travelRate &&
                perDiemRate !== officeRate),
        perDiem: calculatePerDiem(item.hours, perDiemRate),
      };
    });
    const selectedProjectPeriod = resolveActivePeriod(
      selectedDate,
      selectedProject?.BillingPeriod,
    );
    const matchedSavedSection = findSavedStatementSectionForProject(
      projectId,
      selectedProjectPeriod,
    );

    if (matchedSavedSection) {
      const resolvedPeriod = resolveStatementPeriod(
        matchedSavedSection.statement,
        selectedProjectPeriod,
      );

      setStatementContext({
        statement: {
          ...matchedSavedSection.statement,
          BillingStart: resolvedPeriod.billingStart,
          BillingEnd: resolvedPeriod.billingEnd,
          PeriodKey: resolvedPeriod.periodKey,
          StatusID: resolveStatusId(matchedSavedSection.statement) || 1,
        },
        projectId,
        customerId: formik.values.customerId,
        periodLabel:
          matchedSavedSection.periodLabel ||
          `${resolvedPeriod.billingStart} - ${resolvedPeriod.billingEnd}`,
        items: nextItems,
      });
    } else if (projectId) {
      setStatementContext({
        statement: {
          billingStatementId: 0,
          BillingStart: selectedProjectPeriod.billingStart,
          BillingEnd: selectedProjectPeriod.billingEnd,
          PeriodKey: toPeriodKey(
            selectedProjectPeriod.startDate,
            selectedProjectPeriod.endDate,
          ),
          StatusID: 1,
        },
        projectId,
        customerId: formik.values.customerId,
        periodLabel: `${selectedProjectPeriod.billingStart} - ${selectedProjectPeriod.billingEnd}`,
        items: nextItems,
      });
    } else {
      setStatementContext(null);
    }

    formik.setFieldValue("items", nextItems);
  };

  const handleFieldValueChange = (index, field, value) => {
    if (field === "dayRate") manualRateOverrideRef.current.dayRate = true;
    if (field === "mileageRate")
      manualRateOverrideRef.current.mileageRate = true;
    const sourceItems = Array.isArray(latestFormikValuesRef.current?.items)
      ? latestFormikValuesRef.current.items
      : formik.values.items || [];
    const items = sourceItems.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      const updatedItem = { ...item, [field]: value };
      if (field === "hours" || field === "perDiemRate") {
        updatedItem.perDiem = calculatePerDiem(
          updatedItem.hours,
          updatedItem.perDiemRate,
        );
      }
      return updatedItem;
    });

    latestFormikValuesRef.current = {
      ...latestFormikValuesRef.current,
      ...formik.values,
      items,
    };
    formik.setFieldValue("items", items);
  };

  const handleHoursChange = (index, value) => {
    const floatRegex = /^(\d{0,2}(\.\d{0,2})?)$/;
    const numericValue = parseFloat(value);

    if (!(value === "" || floatRegex.test(value))) return;
    if (value === "") {
      handleFieldValueChange(index, "hours", value);
      return;
    }

    // Per-line sanity cap
    if (!Number.isFinite(numericValue) || numericValue > 24) return;

    // Enforce daily hours cap across all line items.
    // This prevents invalid totals from being persisted in local form state.
    // const otherHours = (formik.values.items || []).reduce(
    //   (sum, item, itemIndex) => {
    //     if (itemIndex === index) return sum;
    //     const hours = parseFloat(item?.hours);
    //     return sum + (Number.isFinite(hours) ? hours : 0);
    //   },
    //   0,
    // );

    // if (otherHours + numericValue > DAILY_HOUR_LIMIT) return;

    handleFieldValueChange(index, "hours", value);
  };

  const handleFloatChange = (index, field, value) => {
    const numericValue = parseFloat(value);
    const floatRegex = /^(\d{0,5}(\.\d{0,2})?)$/;

    if (
      !isNaN(value) &&
      (value === "" || floatRegex.test(value)) &&
      (numericValue <= 10000 || value === "")
    ) {
      handleFieldValueChange(index, field, value);
    }
  };

  const handlePerDiemRateChange = (
    index,
    value,
    ratesOverride = null,
    selectionType = null,
  ) => {
    const normalized = formatRate(value);
    const rates = ratesOverride || perDiemRates;
    const officeRate = rates.office;
    const travelRate = rates.travel;
    const sourceItems = Array.isArray(latestFormikValuesRef.current?.items)
      ? latestFormikValuesRef.current.items
      : formik.values.items || [];

    const items = sourceItems.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      const previousRate = formatRate(item?.perDiemRate);
      const sameRate = previousRate === normalized;

      let nextIsOffice = resolveApiBoolean(item?.isOfficePerDiem);
      let nextIsTravel = resolveApiBoolean(item?.isTravelPerDiem);

      if (selectionType === "OFFICE") {
        nextIsOffice = true;
        nextIsTravel = false;
      } else if (selectionType === "TRAVEL") {
        nextIsOffice = false;
        nextIsTravel = true;
      } else if (selectionType === "NA") {
        nextIsOffice = false;
        nextIsTravel = false;
      } else if (!sameRate) {
        const matchesOffice =
          normalized !== "0.00" && normalized === officeRate;
        const matchesTravel =
          normalized !== "0.00" && normalized === travelRate;

        if (matchesOffice && matchesTravel) {
          // If both rates are identical, preserve whichever flag was already set.
          nextIsOffice = resolveApiBoolean(item?.isOfficePerDiem);
          nextIsTravel = resolveApiBoolean(item?.isTravelPerDiem);
          if (!nextIsOffice && !nextIsTravel) nextIsOffice = true;
        } else {
          nextIsOffice = matchesOffice;
          nextIsTravel = matchesTravel;
        }
      } else if (normalized === "0.00") {
        nextIsOffice = false;
        nextIsTravel = false;
      }

      const updatedItem = {
        ...item,
        perDiemRate: normalized,
        isOfficePerDiem: nextIsOffice,
        isTravelPerDiem: nextIsTravel,
      };
      updatedItem.perDiem = calculatePerDiem(
        updatedItem.hours,
        updatedItem.perDiemRate,
      );
      return updatedItem;
    });

    latestFormikValuesRef.current = {
      ...latestFormikValuesRef.current,
      ...formik.values,
      items,
    };
    formik.setFieldValue("items", items);
  };

  const getSelectedPerDiemOptionValue = (item, rates) => {
    const officeRate = formatRate(rates?.office ?? 0);
    const travelRate = formatRate(rates?.travel ?? 0);
    const currentValue = formatRate(getSelectedPerDiemValue(item, rates));
    const isOffice = resolveApiBoolean(item?.isOfficePerDiem);
    const isTravel = resolveApiBoolean(item?.isTravelPerDiem);

    if (currentValue === "0.00") return "NA";
    if (isOffice && currentValue === officeRate) return "OFFICE";
    if (isTravel && currentValue === travelRate) return "TRAVEL";
    if (!isOffice && !isTravel && currentValue === officeRate) return "OFFICE";
    if (!isOffice && !isTravel && currentValue === travelRate) return "TRAVEL";
    return "";
  };

  const resolvePerDiemSelection = (selectionValue, ratesOverride) => {
    const rates = ratesOverride || perDiemRates;
    const officeRate = formatRate(rates?.office ?? 0);
    const travelRate = formatRate(rates?.travel ?? 0);

    if (selectionValue === "OFFICE") {
      return { rate: officeRate, type: "OFFICE" };
    }

    if (selectionValue === "TRAVEL") {
      return { rate: travelRate, type: "TRAVEL" };
    }

    if (selectionValue === "NA") {
      return { rate: "0.00", type: "NA" };
    }

    if (String(selectionValue || "").startsWith("CUSTOM:")) {
      const customRate =
        String(selectionValue).slice("CUSTOM:".length) || "0.00";
      return { rate: formatRate(customRate), type: "CUSTOM" };
    }

    return { rate: formatRate(selectionValue), type: null };
  };

  const getSelectedPerDiemValue = (item, rates) => {
    const officeRate = formatRate(rates?.office ?? 0);
    const travelRate = formatRate(rates?.travel ?? 0);
    const savedRate = formatRate(item?.perDiemRate);

    const isOffice = resolveApiBoolean(item?.isOfficePerDiem);
    const isTravel = resolveApiBoolean(item?.isTravelPerDiem);

    if (isOffice && savedRate === officeRate) return officeRate;
    if (isTravel && savedRate === travelRate) return travelRate;

    return savedRate;
  };

  const getPerDiemSelectOptions = (item, ratesOverride) => {
    const rates = ratesOverride || perDiemRates;
    const officeRate = formatRate(rates?.office ?? 0);
    const travelRate = formatRate(rates?.travel ?? 0);
    const options = [
      { value: "NA", label: "0.00" },
      { value: "OFFICE", label: officeRate },
      { value: "TRAVEL", label: travelRate },
    ];

    return options;
  };

  const getPerDiemMenuItems = (item, ratesOverride, onSelect) => {
    const rates = ratesOverride || perDiemRates;
    const officeRate = formatRate(rates?.office ?? 0);
    const travelRate = formatRate(rates?.travel ?? 0);

    return (
      <>
        <ListSubheader className="per-diem-header">N/A</ListSubheader>
        <MenuItem value="NA" onClick={() => onSelect?.("NA")}>
          0.00
        </MenuItem>
        <ListSubheader className="per-diem-header">
          Office Per Diem
        </ListSubheader>
        <MenuItem value="OFFICE" onClick={() => onSelect?.("OFFICE")}>
          {officeRate}
        </MenuItem>
        <ListSubheader className="per-diem-header">
          Travel Per Diem
        </ListSubheader>
        <MenuItem value="TRAVEL" onClick={() => onSelect?.("TRAVEL")}>
          {travelRate}
        </MenuItem>
      </>
    );
  };

  const handleIntChange = (index, field, value) => {
    const numericValue = parseInt(value, 10);
    const intRegex = /^\d{0,5}$/;

    if (
      !isNaN(value) &&
      (value === "" || intRegex.test(value)) &&
      (numericValue <= 10000 || value === "")
    ) {
      handleFieldValueChange(index, field, value);
    }
  };

  const isSavedSectionEditable = (section) =>
    getSavedSectionStatusId(section) === 1;
  const canRemoveSavedSection = (section) =>
    visibleCardCount > 1 &&
    !isDeleteBlockedStatus(getSavedSectionStatusId(section));
  const createDraftSection = () => {
    const clientSectionKey = createClientSectionKey("draft");
    return {
      sectionKey: clientSectionKey,
      clientSectionKey,
      createdAt: Date.now(),
      customerId: "",
      customerName: "",
      projectId: "",
      projectName: "",
      availableProjects: [],
      perDiemRates: { office: "0.00", travel: "0.00" },
      statement: {
        billingStatementId: 0,
        BillingStart: "",
        BillingEnd: "",
        StatusID: 1,
      },
      periodLabel: "",
      items: [createBlankLineItem(selectedDayKey)],
    };
  };

  const updateSavedSection = (sectionIndex, updater) => {
    setSavedDaySections((prev) =>
      sortSectionsForDisplay(
        (Array.isArray(prev) ? prev : []).map((section, sIdx) =>
          sIdx === sectionIndex && typeof updater === "function"
            ? updater(section)
            : section,
        ),
      ),
    );
  };

  const updateSavedSectionItem = (sectionIndex, itemIndex, updater) => {
    setSavedDaySections((prev) =>
      sortSectionsForDisplay(
        (Array.isArray(prev) ? prev : []).map((section, sIdx) => {
          if (sIdx !== sectionIndex) return section;
          const nextItems = (
            Array.isArray(section?.items) ? section.items : []
          ).map((item, iIdx) => {
            if (iIdx !== itemIndex) return item;
            return typeof updater === "function"
              ? updater(item, section)
              : item;
          });
          return { ...section, items: nextItems };
        }),
      ),
    );
  };

  const handleSavedSectionFieldValueChange = (
    sectionIndex,
    itemIndex,
    field,
    value,
  ) => {
    if (field === "dayRate") manualRateOverrideRef.current.dayRate = true;
    if (field === "mileageRate")
      manualRateOverrideRef.current.mileageRate = true;
    if (field === "billingType" && value) {
      setDraftFieldTouched(sectionIndex, "billingType", false);
    }
    updateSavedSectionItem(sectionIndex, itemIndex, (item) => {
      const updatedItem = { ...item, [field]: value };
      if (field === "hours" || field === "perDiemRate") {
        updatedItem.perDiem = calculatePerDiem(
          updatedItem.hours,
          updatedItem.perDiemRate,
        );
      }
      return updatedItem;
    });
  };

  const handleSavedSectionCustomerChange = async (sectionIndex, customerId) => {
    const selectedCustomerOption =
      customers.find(
        (customer) => String(customer.CustomerID) === String(customerId),
      ) || null;

    let projects = [];
    try {
      projects = customerId
        ? await fetchProjectsForCustomer(customerId, assignedProjects)
        : [];
    } catch (error) {
      const apiError = parseBillingApiError(error);
      showToast(
        apiError?.message?.message ||
          apiError?.message ||
          "Unable to load projects.",
        "error",
      );
    }

    updateSavedSection(sectionIndex, (section) => ({
      ...section,
      customerId,
      customerName: selectedCustomerOption?.CustomerName || "",
      projectId: "",
      projectName: "",
      availableProjects: projects,
      periodLabel: "",
      perDiemRates: { office: "0.00", travel: "0.00" },
      statement: {
        billingStatementId: 0,
        BillingStart: "",
        BillingEnd: "",
        StatusID: 1,
      },
      items: (Array.isArray(section?.items) ? section.items : []).map(
        (item) => ({
          ...item,
          dayRate: "0.00",
          perDiemRate: "0.00",
          perDiem: "0.00",
          isOfficePerDiem: false,
          isTravelPerDiem: false,
        }),
      ),
    }));
    setDraftFieldTouched(sectionIndex, "customerId", false);
    setDraftFieldTouched(sectionIndex, "projectId", false);
  };

  const handleSavedSectionProjectChange = async (sectionIndex, projectId) => {
    const section =
      flatSavedSections?.[sectionIndex] || savedDaySections?.[sectionIndex];
    const normalizedProjectId = String(projectId || "");
    const availableSectionProjects = Array.isArray(section?.availableProjects)
      ? section.availableProjects
      : [];
    const selectedProjectOption =
      availableSectionProjects.find(
        (project) =>
          String(project.ProjectId ?? project.ProjectID) ===
          String(normalizedProjectId),
      ) || null;

    if (normalizedProjectId) {
      const selectedCustomerOption =
        customers.find(
          (customer) =>
            String(customer.CustomerID) === String(section?.customerId),
        ) || null;
      const validation = await validateProjectSelectionForDate({
        projectId: normalizedProjectId,
        customerName:
          section?.customerName ||
          selectedCustomerOption?.CustomerName ||
          "this customer",
        projectName:
          selectedProjectOption?.Name ||
          selectedProjectOption?.ProjectName ||
          "this project",
      });
      if (!validation.allowed) {
        return;
      }
    }

    let resolvedDayRate = parseNumber(selectedProjectOption?.BillingDayRate);
    let resolvedOfficePerDiem = parseNumber(
      selectedProjectOption?.BillableOfficePerDiem ??
        selectedProjectOption?.PayableOfficePerDiem ??
        0,
    );
    let resolvedTravelPerDiem = parseNumber(
      selectedProjectOption?.BillableTravelPerDiem ??
        selectedProjectOption?.PayableTravelPerDiem ??
        0,
    );

    if (normalizedProjectId) {
      try {
        const response = await getProjectDetailsCached(normalizedProjectId);
        const rates = resolveProjectRatesFromProjectByIdResponse(response);
        resolvedDayRate = rates.dayRate;
        resolvedOfficePerDiem = rates.officePerDiem;
        resolvedTravelPerDiem = rates.travelPerDiem;
      } catch (error) {
        // Keep already available project-list values for draft sections.
      }
    }

    const nextDayRate = resolvedDayRate ? formatRate(resolvedDayRate) : "0.00";
    const selectedProjectPeriod = resolveActivePeriod(
      selectedDate,
      selectedProjectOption?.BillingPeriod,
    );
    const matchedSavedSection = findSavedStatementSectionForProject(
      normalizedProjectId,
      selectedProjectPeriod,
    );
    const matchedSavedPeriod = matchedSavedSection
      ? resolveStatementPeriod(
          matchedSavedSection.statement,
          selectedProjectPeriod,
        )
      : null;
    updateSavedSection(sectionIndex, (currentSection) => ({
      ...currentSection,
      projectId: normalizedProjectId,
      projectName:
        selectedProjectOption?.Name || selectedProjectOption?.ProjectName || "",
      periodLabel: normalizedProjectId
        ? matchedSavedSection?.periodLabel ||
          `${(matchedSavedPeriod || selectedProjectPeriod).billingStart} - ${(matchedSavedPeriod || selectedProjectPeriod).billingEnd}`
        : "",
      perDiemRates: {
        office: formatRate(resolvedOfficePerDiem),
        travel: formatRate(resolvedTravelPerDiem),
      },
      statement: normalizedProjectId
        ? matchedSavedSection
          ? {
              ...matchedSavedSection.statement,
              BillingStart: matchedSavedPeriod?.billingStart,
              BillingEnd: matchedSavedPeriod?.billingEnd,
              PeriodKey: matchedSavedPeriod?.periodKey,
              StatusID: resolveStatusId(matchedSavedSection.statement) || 1,
            }
          : {
              billingStatementId: 0,
              BillingStart: selectedProjectPeriod.billingStart,
              BillingEnd: selectedProjectPeriod.billingEnd,
              PeriodKey: toPeriodKey(
                selectedProjectPeriod.startDate,
                selectedProjectPeriod.endDate,
              ),
              StatusID: 1,
            }
        : {
            billingStatementId: 0,
            BillingStart: "",
            BillingEnd: "",
            StatusID: 1,
          },
      items: (Array.isArray(currentSection?.items)
        ? currentSection.items
        : []
      ).map((item) => ({
        ...item,
        dayRate: nextDayRate,
        perDiemRate: "0.00",
        perDiem: "0.00",
        isOfficePerDiem: false,
        isTravelPerDiem: false,
      })),
    }));
    setDraftFieldTouched(sectionIndex, "projectId", false);
  };

  const handleSavedSectionHoursChange = (sectionIndex, itemIndex, value) => {
    const floatRegex = /^(\d{0,2}(\.\d{0,2})?)$/;
    const numericValue = parseFloat(value);
    if (!(value === "" || floatRegex.test(value))) return;
    if (value !== "" && (!Number.isFinite(numericValue) || numericValue > 24))
      return;
    handleSavedSectionFieldValueChange(sectionIndex, itemIndex, "hours", value);
  };

  const handleSavedSectionFloatChange = (
    sectionIndex,
    itemIndex,
    field,
    value,
  ) => {
    const numericValue = parseFloat(value);
    const floatRegex = /^(\d{0,5}(\.\d{0,2})?)$/;
    if (
      !isNaN(value) &&
      (value === "" || floatRegex.test(value)) &&
      (numericValue <= 10000 || value === "")
    ) {
      handleSavedSectionFieldValueChange(sectionIndex, itemIndex, field, value);
    }
  };

  const handleSavedSectionIntChange = (
    sectionIndex,
    itemIndex,
    field,
    value,
  ) => {
    const numericValue = parseInt(value, 10);
    const intRegex = /^\d{0,5}$/;
    if (
      !isNaN(value) &&
      (value === "" || intRegex.test(value)) &&
      (numericValue <= 10000 || value === "")
    ) {
      handleSavedSectionFieldValueChange(sectionIndex, itemIndex, field, value);
    }
  };

  const handleSavedSectionPerDiemRateChange = (
    sectionIndex,
    itemIndex,
    value,
    ratesOverride,
    selectionType = null,
  ) => {
    const rates = ratesOverride || perDiemRates;
    const officeRate = formatRate(rates?.office ?? 0);
    const travelRate = formatRate(rates?.travel ?? 0);
    const normalized = formatRate(value);

    updateSavedSectionItem(sectionIndex, itemIndex, (item) => {
      const previousRate = formatRate(item?.perDiemRate);
      const sameRate = previousRate === normalized;

      let nextIsOffice = resolveApiBoolean(item?.isOfficePerDiem);
      let nextIsTravel = resolveApiBoolean(item?.isTravelPerDiem);

      if (selectionType === "OFFICE") {
        nextIsOffice = true;
        nextIsTravel = false;
      } else if (selectionType === "TRAVEL") {
        nextIsOffice = false;
        nextIsTravel = true;
      } else if (selectionType === "NA") {
        nextIsOffice = false;
        nextIsTravel = false;
      } else if (!sameRate) {
        const matchesOffice =
          normalized !== "0.00" && normalized === officeRate;
        const matchesTravel =
          normalized !== "0.00" && normalized === travelRate;

        if (matchesOffice && matchesTravel) {
          if (!nextIsOffice && !nextIsTravel) nextIsOffice = true;
        } else {
          nextIsOffice = matchesOffice;
          nextIsTravel = matchesTravel;
        }
      } else if (normalized === "0.00") {
        nextIsOffice = false;
        nextIsTravel = false;
      }

      const updatedItem = {
        ...item,
        perDiemRate: normalized,
        isOfficePerDiem: nextIsOffice,
        isTravelPerDiem: nextIsTravel,
      };
      updatedItem.perDiem = calculatePerDiem(
        updatedItem.hours,
        updatedItem.perDiemRate,
      );
      return updatedItem;
    });
  };

  const addMoreItem = async () => {
    if (isDeleting || isAddingMore) return;
    setIsAddingMore(true);
    try {
      if (isLocked) {
        showToast("This statement is closed and cannot be modified.", "error");
        return;
      }
      if (!shouldShowStandaloneForm) {
        const hasValidDraftSections = validateDraftSavedSections();
        if (!hasValidDraftSections) return;
        setSavedDaySections((prev) =>
          sortSectionsForDisplay([
            ...(Array.isArray(prev) ? prev : []),
            createDraftSection(),
          ]),
        );
        return;
      }
      const isValid = await ensureStandaloneFormValid();
      if (!isValid) return;
      // Add More = create a brand-new section.
      // Preserve the current section (saved or unsaved) as a read-only block,
      // then reset the form to a fresh, independent entry.
      const rateOverrideMatches =
        projectRateOverride &&
        String(projectRateOverride.projectId) ===
          String(formik.values.projectId);
      const displayRates = rateOverrideMatches
        ? {
            dayRate: projectRateOverride.dayRate,
            officePerDiem: projectRateOverride.officePerDiem,
            travelPerDiem: projectRateOverride.travelPerDiem,
          }
        : null;

      const period = resolveActivePeriod(
        selectedDate,
        selectedProject?.BillingPeriod,
      );
      const sectionStatement = formik.values.projectId
        ? {
            billingStatementId: 0,
            BillingStart:
              statementContext?.statement?.BillingStart || period.billingStart,
            BillingEnd:
              statementContext?.statement?.BillingEnd || period.billingEnd,
            PeriodKey:
              statementContext?.statement?.PeriodKey ||
              toPeriodKey(period.startDate, period.endDate),
            StatusID: 1,
          }
        : {
            billingStatementId: 0,
            BillingStart: "",
            BillingEnd: "",
            PeriodKey: "",
            StatusID: 1,
          };

      const sectionToPreserve = {
        clientSectionKey: createClientSectionKey(formik.values.projectId),
        createdAt: Date.now(),
        customerId: formik.values.customerId || "",
        customerName: selectedCustomer?.CustomerName || "",
        projectId: formik.values.projectId || "",
        projectName: selectedProject?.Name || "",
        availableProjects: Array.isArray(availableProjects)
          ? deepClone(availableProjects)
          : [],
        ...(displayRates
          ? {
              displayRates,
              perDiemRates: {
                office: formatRate(displayRates.officePerDiem),
                travel: formatRate(displayRates.travelPerDiem),
              },
            }
          : {
              perDiemRates: {
                office: perDiemRates.office,
                travel: perDiemRates.travel,
              },
            }),
        statement: sectionStatement,
        periodLabel:
          statementContext?.periodLabel ||
          (formik.values.projectId
            ? `${period.billingStart} - ${period.billingEnd}`
            : ""),
        items: Array.isArray(formik.values.items)
          ? deepClone(formik.values.items)
          : [],
      };

      const splitSectionToPreserve = explodeSectionsByItem([sectionToPreserve]);

      setSavedDaySections((prev) => {
        const existing = Array.isArray(prev) ? prev : [];
        return sortSectionsForDisplay([...existing, ...splitSectionToPreserve]);
      });

      setStatementContext(null);
      setAvailableProjects([]);
      setProjectRateOverride(null);
      setDraftSectionTouched({});
      const nextSeed = getDefaultForm(selectedDate);
      setFormSeed(nextSeed);
      formik.resetForm({ values: deepClone(nextSeed) });
      setShowManualSection(true);
    } finally {
      setIsAddingMore(false);
    }
  };
  const handleRemoveCurrentFormCard = () => {
    if (visibleCardCount <= 1) return;
    const nextSeed = getDefaultForm(selectedDate);
    setDraftSectionTouched({});
    setStatementContext(null);
    setProjectRateOverride(null);
    setAvailableProjects([]);
    setFormSeed(nextSeed);
    formik.resetForm({ values: deepClone(nextSeed) });
    setShowManualSection(!hasLineItems);
  };
  const handleClockHours = async () => {
    if (!hasPendingEditableEntry) {
      return;
    }
    if (isLocked) {
      showToast("This statement is closed and cannot be modified.", "error");
      return;
    }
    if (shouldShowStandaloneForm) {
      const isValid = await ensureStandaloneFormValid();
      if (!isValid) return;
    }
    if (!validateDraftSavedSections()) return;
    await formik.submitForm();
  };

  const navigateCalendar = (direction) => {
    if (viewMode === "week") {
      const nextDate = new Date(selectedDate);
      nextDate.setDate(selectedDate.getDate() + direction * 7);
      setSelectedDate(nextDate);
      setCurrentMonthDate(
        new Date(nextDate.getFullYear(), nextDate.getMonth(), 1),
      );
      return;
    }

    if (viewMode === "cycle") {
      const nextDate = getAdjacentCycleDate(selectedDate, direction);
      setSelectedDate(nextDate);
      setCurrentMonthDate(
        new Date(nextDate.getFullYear(), nextDate.getMonth(), 1),
      );
      return;
    }

    setCurrentMonthDate(
      new Date(
        currentMonthDate.getFullYear(),
        currentMonthDate.getMonth() + direction,
        1,
      ),
    );
  };

  const handleViewModeChange = (mode) => {
    if (mode === "week" && viewMode !== "week") {
      const isCurrentMonth =
        currentMonthDate.getFullYear() === today.getFullYear() &&
        currentMonthDate.getMonth() === today.getMonth();
      setSelectedDate(
        isCurrentMonth ? new Date(today) : new Date(currentMonthDate),
      );
    }
    if (mode === "cycle") {
      // The current month follows today's cycle. Any other month starts at
      // its first cycle, matching the Month -> Week behavior.
      const isCurrentMonth =
        currentMonthDate.getFullYear() === today.getFullYear() &&
        currentMonthDate.getMonth() === today.getMonth();
      const cycleSourceDate = isCurrentMonth ? today : currentMonthDate;
      const cycleDay = cycleSourceDate.getDate() <= 14 ? 1 : 15;
      const cycleDate = new Date(
        currentMonthDate.getFullYear(),
        currentMonthDate.getMonth(),
        cycleDay,
      );
      setSelectedDate(cycleDate);
      setCurrentMonthDate(
        new Date(cycleDate.getFullYear(), cycleDate.getMonth(), 1),
      );
    }
    setViewMode(mode);
  };
  const resetAttachmentEditor = () => {
    setAttachmentFile([]);
    setAttachmentName([]);
    setAttachmentRemark("");
    setAttachmentEditId(0);
    setAttachmentFilePath("");
  };

  const closeAttachmentsModal = () => {
    setIsAttachmentsOpen(false);
    resetAttachmentEditor();
    if (attachmentContextBackup) {
      setStatementContext(attachmentContextBackup.statementContext);
      setFormSeed(attachmentContextBackup.formSeed);
      setAvailableProjects(attachmentContextBackup.availableProjects);
      setSavedDaySections(
        sortSectionsForDisplay(attachmentContextBackup.savedDaySections),
      );
      setAttachmentContextBackup(null);
    }
  };

  const getAttachmentListByProjectId = async () => {
    const attachmentContext = validateAttachmentContext();
    if (!attachmentContext) {
      setAttachmentFileObject([]);
      return;
    }

    setAttachmentLoading(true);
    const config = {
      method: "GET",
      url: GET_ATTACHMENT_LOG_LIST_URL,
      headers: authHeader(),
      params: {
        webString: enCryptFun(
          JSON.stringify({
            userId: billingAttachmentDetail.huserId,
            billingStatementId: attachmentContext.statementId,
            parentTypeId: 1,
          }),
        ),
      },
    };

    try {
      const response = await API(config);
      const parsedResponse = JSON.parse(deCryptFun(response.data));
      if (parsedResponse.status === "Success") {
        setAttachmentFileObject(parsedResponse.data || []);
      }
    } catch (error) {
      const apiError = parseBillingApiError(error);
      const statusCode =
        error?.response?.status ??
        apiError?.status ??
        apiError?.response?.status;
      setAttachmentFileObject([]);
      if (statusCode === 403) {
        // showToast(
        //   "You are not authorized to view attachments for this entry.",
        //   "error",
        // );
      } else if (
        apiError?.message &&
        apiError?.message !== "No record found."
      ) {
        showToast(
          apiError?.message?.message ||
            apiError?.message ||
            "Unable to load attachments.",
          "error",
        );
      }
    } finally {
      setAttachmentLoading(false);
    }
  };

  const handleDownloadAllAttachments = async () => {
    const attachments = (
      Array.isArray(attachmentFileObject) ? attachmentFileObject : []
    )
      .map((item) => ({
        fileName: item?.FileName || item?.fileName || "",
        path: item?.Path || item?.path || "",
      }))
      .filter((item) => item.fileName && item.path);

    if (attachments.length === 0) return;

    try {
      setDownloadAllLoading(true);
      await downloadAllAttachments({
        attachments,
        zipName: `${billingAttachmentDetail?.hProjectName || "billing"}_attachments_${billingAttachmentDetail?.hperiodKey || formatDate(selectedDate)}`,
      });
    } catch (error) {
      showToast("Unable to download attachments.", "error");
    } finally {
      setDownloadAllLoading(false);
    }
  };

  useEffect(() => {
    if (isAttachmentsOpen) {
      getAttachmentListByProjectId();
    }
  }, [isAttachmentsOpen]);

  const handleAttachmentFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files || files.length === 0) return;

    const filenames = files.map((file) => {
      let filename = file.name;
      const maxLength = 100;
      const lastDotIndex = filename.lastIndexOf(".");
      const extension = lastDotIndex !== -1 ? filename.substring(lastDotIndex) : "";
      if (filename.length > maxLength) {
        filename = `${filename.substring(0, maxLength - extension.length - 3)}...${extension}`;
      }
      return filename;
    });

    setAttachmentName((previousNames) => [...previousNames, ...filenames]);
    setAttachmentFile((previousFiles) => [...previousFiles, ...files]);
    event.target.value = null;
  };

  const removePendingAttachmentFile = (index) => {
    setAttachmentFile((files) => files.filter((_, fileIndex) => fileIndex !== index));
    setAttachmentName((names) => names.filter((_, fileIndex) => fileIndex !== index));
  };

  const handleAttachmentUpload = async () => {
    if (isStatementLocked(resolveStatusId(statementContext?.statement))) {
      showToast("This statement is closed and cannot be modified.", "error");
      return;
    }
    if (!attachmentRemark) {
      showToast("Description is required.", "error");
      return;
    }

    if (attachmentFile.length === 0) {
      showToast("Attachment file is required.", "error");
      return;
    }

    const attachmentContext = validateAttachmentContext();
    if (!attachmentContext) return;

    setAttachmentActionLoading(true);
    let uploadedFiles;
    try {
      uploadedFiles = await uploadAttachmentFiles(attachmentFile);
    } catch (error) {
      // Keep the selected files so the user can retry.
      showToast(getUploadErrorMessage(error), "error");
      setAttachmentActionLoading(false);
      return;
    }

    try {
      // One attachment record is saved per uploaded file.
      const config = {
        method: "POST",
        url: UPLOAD_ATTACHMENT_LOG_URL,
        headers: authHeader(),
        data: {
          webString: enCryptFun(
            JSON.stringify({
              attachmentId: 0,
              parentID: attachmentContext.statementId,
              ParentTypeID: 1,
              projID: attachmentContext.projectId,
              userID: billingAttachmentDetail.huserId,
              periodKey: billingAttachmentDetail.hperiodKey,
              description: attachmentRemark,
              files: uploadedFiles,
              isOrphaned: 0,
            }),
          ),
        },
      };

      const response = await API(config);
      const parsedResponse = JSON.parse(deCryptFun(response.data));
      if (parsedResponse.status === "Success") {
        showToast(
          uploadedFiles.length > 1
            ? `${uploadedFiles.length} attachments uploaded successfully`
            : "Attachment uploaded successfully",
          "success",
        );
        resetAttachmentEditor();
        getAttachmentListByProjectId();
      } else {
        showToast(parsedResponse.message || "Unable to save attachments.", "error");
      }
    } catch (error) {
      const apiError = parseBillingApiError(error);

      const statusCode =
        error?.response?.status ??
        apiError?.status ??
        apiError?.response?.status;

      // Extract correct backend message
      const backendMessage =
        apiError?.message?.message ||
        error?.response?.data?.message?.message ||
        error?.response?.data?.message ||
        apiError?.message ||
        "Unable to upload attachment.";

      // Check access denied / locked statement
      const isAccessDenied =
        statusCode === 403 ||
        apiError?.message?.accessDenied === 2 ||
        error?.response?.data?.message?.accessDenied === 2;

      // Show exact backend message
      showToast(backendMessage, "error");

      // Close attachment modal if locked / access denied
      if (isAccessDenied) {
        setAttachmentName([]);
        setIsBillingOpen(false);
        setIsAttachmentsOpen(false);
      }
    } finally {
      setAttachmentActionLoading(false);
    }
  };

  const handleAttachmentEdit = (item) => {
    if (isStatementLocked(resolveStatusId(statementContext?.statement))) {
      showToast("This statement is closed and cannot be modified.", "error");
      return;
    }
    setAttachmentEditId(item.attachmentId);
    setAttachmentName(item.FileName ? [item.FileName] : []);
    setAttachmentRemark(item.Description || "");
    setAttachmentFilePath(item.Path || "");
  };

  const handleAttachmentUpdate = async () => {
    if (isStatementLocked(resolveStatusId(statementContext?.statement))) {
      showToast("This statement is closed and cannot be modified.", "error");
      return;
    }
    if (!attachmentRemark) {
      showToast(
        "Cannot save file attachment. Attachment description is required.",
        "error",
      );
      return;
    }

    const attachmentContext = validateAttachmentContext();
    if (!attachmentContext) return;

    try {
      setAttachmentActionLoading(true);
      const response = await API({
        method: "POST",
        url: UPLOAD_ATTACHMENT_LOG_URL,
        headers: authHeader(),
        data: {
          webString: enCryptFun(
            JSON.stringify({
              attachmentId: attachmentEditId,
              parentID: attachmentContext.statementId,
              ParentTypeID: 1,
              projID: attachmentContext.projectId,
              userID: billingAttachmentDetail.huserId,
              periodKey: billingAttachmentDetail.hperiodKey,
              // Edits rename one attachment; the file itself is not replaced.
              fileName: attachmentName[0] ?? "",
              description: attachmentRemark,
              isOrphaned: 0,
            }),
          ),
        },
      });
      const parsedResponse = JSON.parse(deCryptFun(response.data));
      if (parsedResponse.status === "Success") {
        showToast("Description updated successfully", "success");
        resetAttachmentEditor();
        getAttachmentListByProjectId();
      }
    } catch (error) {
      const apiError = parseBillingApiError(error);
      const statusCode =
        error?.response?.status ??
        apiError?.status ??
        apiError?.response?.status;
      showToast(
        statusCode === 403
          ? "You are not authorized to update attachments for this entry."
          : apiError?.message?.message ||
              apiError?.message ||
              "Unable to update attachment.",
        "error",
      );
    } finally {
      setAttachmentActionLoading(false);
    }
  };

  const handleAttachmentDelete = async (attachmentId) => {
    if (isStatementLocked(resolveStatusId(statementContext?.statement))) {
      showToast("This statement is closed and cannot be modified.", "error");
      return;
    }
    try {
      setSelectedAttachmentId(attachmentId);
      await API({
        method: "DELETE",
        url: DELETE_ATTACHMENT_LOG_BY_ID_URL,
        headers: authHeader(),
        params: {
          webString: enCryptFun(
            JSON.stringify({ attachmentId, isOrphaned: 0 }),
          ),
        },
      });
      showToast("Attachment removed successfully", "success");
      setAttachmentFileObject((prev) =>
        prev.filter((item) => item.attachmentId !== attachmentId),
      );
      if (selectedAttachmentId === attachmentId) {
        setSelectedAttachmentId(null);
      }
    } catch (error) {
      const apiError = parseBillingApiError(error);
      showToast(apiError?.message?.message || apiError?.message, "error");
    }
  };
  const handleAttachmentOpen = () => {
    if (isDeleting) return;
    if (!statementContext?.statement?.billingStatementId) {
      showToast(
        "Please save the billing entry before viewing or uploading attachments.",
        "error",
      );
      return;
    }

    setIsAttachmentsOpen(true);
  };

  const handleAttachmentOpenForSection = (section) => {
    if (isDeleting) return;
    const statementId = section?.statement?.billingStatementId;
    if (!statementId) {
      showToast(
        "Please save the billing entry before viewing or uploading attachments.",
        "error",
      );
      return;
    }

    // Temporarily point attachment logic at the chosen section; restore on close.
    setAttachmentContextBackup({
      statementContext,
      formSeed,
      availableProjects,
      savedDaySections,
    });
    setStatementContext({
      statement: section.statement,
      projectId: section.projectId,
      customerId: section.customerId,
      periodLabel: section.periodLabel,
      items: section.items,
    });
    setIsAttachmentsOpen(true);
  };

  const handleDeleteSavedSection = async (section) => {
    if (isDeleting) return;
    const statusId = getSavedSectionStatusId(section);
    if (isDeleteBlockedStatus(statusId)) {
      showToast(
        "This billing entry cannot be deleted once the statement is closed or invoicing has started.",
        "error",
      );
      return;
    }
    if (!canRemoveSavedSection(section)) {
      showToast("At least one billing card must remain.", "error");
      return;
    }
    const detailId = getSectionDetailId(section);
    const sectionKey = section?.sectionKey;
    const statementId = Number(section?.statement?.billingStatementId || 0);
    setIsDeleting(true);

    if (!detailId) {
      setSavedDaySections((prev) =>
        sortSectionsForDisplay(
          explodeSectionsByItem(prev || []).filter((itemSection) =>
            sectionKey
              ? itemSection?.sectionKey !== sectionKey
              : getSectionDetailId(itemSection) !== detailId,
          ),
        ),
      );
      showToast("Line item deleted", "success");
      setIsDeleting(false);
      return;
    }

    const dateKey = formatDate(selectedDate);
    setDeletingDetailId(detailId);

    try {
      // 1) deleteBillingStatementDetailsById
      await API({
        method: "DELETE",
        url: DELETE_BILLING_STATEMENT_BY_ID,
        headers: authHeader(),
        params: {
          webString: enCryptFun(
            JSON.stringify({
              billingStatementDetailsId: detailId,
              isOrphaned: 0,
            }),
          ),
        },
      });

      // Remove deleted card immediately from UI.
      setSavedDaySections((prev) =>
        sortSectionsForDisplay(
          explodeSectionsByItem(prev || []).filter(
            (itemSection) => getSectionDetailId(itemSection) !== detailId,
          ),
        ),
      );

      // 2) getDailyEntryBillingByDate
      let dayResponse = { data: [] };
      try {
        dayResponse = await fetchDailyEntryBillingByDay({ date: dateKey });
      } catch (error) {
        const apiError = parseBillingApiError(error);
        const noRecord = String(
          apiError?.message?.message || apiError?.message || "",
        )
          .toLowerCase()
          .includes("no record found");
        if (!noRecord) throw error;
      }

      // 3) common/systemLogInsert
      await API({
        method: "POST",
        url: SYSTEM_LOG_INSERT_URL,
        headers: authHeader(),
        data: {
          webString: enCryptFun(
            JSON.stringify({
              logTypeID: 47,
              logUser: getLogUserId(),
              target: statementId || 0,
              target2: section?.customerId || 0,
              description: `Line item removed in project '${section?.projectName || ""}' for customer '${section?.customerName || ""}'.`,
            }),
          ),
        },
      });

      // 4) getProjectById
      const projectResponse = await getProjectDetailsCached(
        section?.projectId,
        {
          forceRefresh: true,
        },
      );
      const loggedInUserId = getLogUserId();
      const broker = (projectResponse?.BrokerDetails || []).find(
        (item) => String(item?.UserID) === String(loggedInUserId),
      );
      const project = projectResponse?.ProjectDetails?.[0] || {};
      const resolvedRates =
        resolveProjectRatesFromProjectByIdResponse(projectResponse);
      const resolvedMileageRate = parseNumber(
        broker?.MileageRate ??
          broker?.mileageRate ??
          project?.MileageRate ??
          project?.mileageRate ??
          section?.rates?.mileageRate ??
          0,
      );

      const fallbackPeriod = resolveActivePeriod(
        selectedDate,
        project?.BillingPeriod,
      );
      const resolvedPeriod = resolveStatementPeriod(
        section?.statement,
        fallbackPeriod,
      );

      // Build remaining full-period details for this statement.
      const dayEntries = Array.isArray(dayResponse?.data)
        ? dayResponse.data
        : [];
      const matchingEntry = dayEntries.find((entry) => {
        const entryStatementId = Number.parseInt(
          entry?.billingStatementId ??
            entry?.BillingStatementId ??
            entry?.BillingStatementID ??
            entry?.HeaderID ??
            entry?.headerId ??
            0,
          10,
        );
        return entryStatementId === statementId;
      });

      let remainingDetailsRaw = [];
      if (
        typeof matchingEntry?.billingDetails === "string" &&
        matchingEntry.billingDetails
      ) {
        try {
          remainingDetailsRaw = JSON.parse(matchingEntry.billingDetails);
        } catch (error) {
          remainingDetailsRaw = [];
        }
      } else if (Array.isArray(matchingEntry?.billingDetails)) {
        remainingDetailsRaw = matchingEntry.billingDetails;
      }

      const normalizedDetails = (
        Array.isArray(remainingDetailsRaw) ? remainingDetailsRaw : []
      ).map((detail) => {
        const normalizedDate = formatApiDateKey(
          detail?.Date ?? detail?.date,
          dateKey,
        );
        const hours = parseNumber(detail?.Hours ?? detail?.hours);
        const perDiemRate = parseNumber(
          detail?.PerDiemRate ?? detail?.perDiemRate,
        );
        const perDiem = parseNumber(
          detail?.PerDiem ??
            detail?.perDiem ??
            calculatePerDiem(hours, perDiemRate),
        );
        const lodging = parseNumber(detail?.Lodging ?? detail?.lodging);
        const copies = parseNumber(detail?.Copies ?? detail?.copies);
        const misc = parseNumber(detail?.Misc ?? detail?.misc);
        const miles = parseNumber(detail?.Miles ?? detail?.miles);
        const dayRate = parseNumber(
          detail?.DayRate ?? detail?.dayRate ?? resolvedRates.dayRate ?? 0,
        );
        const mileageRate = parseNumber(
          detail?.MileageRate ??
            detail?.mileageRate ??
            resolvedMileageRate ??
            0,
        );
        const hoursCal = Math.min(hours, DAILY_HOUR_LIMIT);
        const lineTotal =
          perDiem +
          lodging +
          copies +
          misc +
          miles * mileageRate +
          (hoursCal / DAILY_HOUR_LIMIT) * dayRate;

        return {
          billingStatementDetailsId:
            detail?.billingStatementDetailsId ??
            detail?.BillingStatementDetailsId ??
            0,
          headerId: statementId || 0,
          billingType: Number(
            detail?.BillingType ??
              detail?.billingType ??
              detail?.BillingTypeId ??
              detail?.billingTypeId ??
              0,
          ),
          date: normalizedDate,
          hours: Number(hours),
          perDiemRate: Number(perDiemRate),
          perDiem: Number(perDiem),
          lodging: Number(lodging),
          miles: Number(miles),
          copies: Number(copies),
          misc: Number(misc),
          description: detail?.Description ?? detail?.description ?? "",
          isOrphaned: 0,
          isTravelPerDiem: resolveApiBoolean(
            detail?.IsTravelPerDiem ??
              detail?.isTravelPerDiem ??
              detail?.sTravelPerDiem ??
              detail?.sTravelPerdiem,
          ),
          isOfficePerDiem: resolveApiBoolean(
            detail?.IsOfficePerDiem ??
              detail?.isOfficePerDiem ??
              detail?.sOfficePerDiem ??
              detail?.sOfficePerdiem,
          ),
          _lineTotal: lineTotal,
        };
      });

      const totals = normalizedDetails.reduce(
        (acc, detail) => {
          acc.hours += parseNumber(detail?.hours);
          acc.perDiem += parseNumber(detail?.perDiem);
          acc.lodging += parseNumber(detail?.lodging);
          acc.miles += parseNumber(detail?.miles);
          acc.misc += parseNumber(detail?.misc);
          acc.copies += parseNumber(detail?.copies);
          acc.total += parseNumber(detail?._lineTotal);
          return acc;
        },
        {
          hours: 0,
          perDiem: 0,
          lodging: 0,
          miles: 0,
          misc: 0,
          copies: 0,
          total: 0,
        },
      );

      // 5) /billStatement/upsertDailyEntryStatement
      await upsertDailyEntryStatement({
        statementDetails: [
          {
            billingStatementId: statementId || 0,
            userId: getLogUserId(),
            periodKey:
              resolvedPeriod?.periodKey ||
              toPeriodKey(fallbackPeriod.startDate, fallbackPeriod.endDate),
            projectId:
              Number.parseInt(section?.projectId, 10) || section?.projectId,
            statusId: resolveStatusId(section?.statement) || 1,
            billingStart:
              resolvedPeriod?.billingStart || fallbackPeriod.billingStart,
            billingEnd: resolvedPeriod?.billingEnd || fallbackPeriod.billingEnd,
            hours: totals.hours,
            dayRate: parseNumber(resolvedRates.dayRate),
            perDiem: totals.perDiem,
            lodging: totals.lodging,
            miles: totals.miles,
            misc: totals.misc,
            copies: totals.copies,
            mileageRate: resolvedMileageRate,
            total: totals.total,
            isOrphaned: false,
            billingDetails: normalizedDetails.map(
              ({ _lineTotal, ...detail }) => detail,
            ),
          },
        ],
      });

      await refreshCalendarEntries({
        anchorDate: selectedDate,
        mode: viewMode,
      });
      await hydratePopup(
        selectedDate,
        section?.customerId || formik.values.customerId,
        section?.projectId || formik.values.projectId,
      );
      showToast("Line item deleted", "success");
    } catch (error) {
      const apiError = parseBillingApiError(error);
      showToast(
        apiError?.message?.message ||
          apiError?.message ||
          "Unable to delete billing detail.",
        "error",
      );
      await hydratePopup(
        selectedDate,
        formik.values.customerId,
        formik.values.projectId,
      );
    } finally {
      setDeletingDetailId(null);
      setIsDeleting(false);
    }
  };
  const renderAttachmentModal = () => (
    <div className="daily-entry-modal">
      <div
        className="daily-entry-modal-backdrop"
        onClick={closeAttachmentsModal}
      />
      <div className="daily-entry-modal-card attachments-modal-card">
        <div className="attachments-header">
          <span className="attachments-count">
            {attachmentFileObject.length.toString().padStart(2, "0")}{" "}
            <span style={{ color: "#212121" }}>attachments found</span>
          </span>
          <Tooltip title="Download all attachments.">
            <button
              className="attachments-download"
              type="button"
              onClick={handleDownloadAllAttachments}
              disabled={
                downloadAllLoading ||
                !attachmentFileObject ||
                attachmentFileObject.length === 0
              }
              style={{
                opacity:
                  downloadAllLoading ||
                  !attachmentFileObject ||
                  attachmentFileObject.length === 0
                    ? 0.5
                    : 1,
                cursor:
                  downloadAllLoading ||
                  !attachmentFileObject ||
                  attachmentFileObject.length === 0
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              <img
                className="attachments-download-icon"
                src={downloadIcon}
                alt=""
              />
            </button>
          </Tooltip>
        </div>
        <div className="attachments-body">
          <div className="attachments-upload">
            <h3>Upload New Attachment</h3>
            <div className="file-row">
              <Button
                className={
                  isStatementLocked(
                    resolveStatusId(statementContext?.statement),
                  )
                    ? "disable-choose-file"
                    : "choose-file"
                }
                component="label"
                type="button"
                disabled={isStatementLocked(
                  resolveStatusId(statementContext?.statement),
                )}
              >
                Choose File
                <input
                  hidden
                  type="file"
                  multiple
                  onChange={handleAttachmentFileChange}
                  disabled={isStatementLocked(
                    resolveStatusId(statementContext?.statement),
                  )}
                />
              </Button>
              {attachmentName.length > 0 ? (
                <div className="attachment-pending-files">
                  {attachmentName.map((filename, index) => (
                    <Chip
                      key={`${filename}-${index}`}
                      className="file-chip"
                      label={filename}
                      onDelete={() => removePendingAttachmentFile(index)}
                      deleteIcon={
                        <img
                          className="attachments-cancel-icon"
                          src={cancelIcon}
                          alt=""
                        />
                      }
                    />
                  ))}
                </div>
              ) : null}
            </div>
            <textarea
              placeholder="Write description"
              value={attachmentRemark}
              onChange={(event) => setAttachmentRemark(event.target.value)}
              disabled={isStatementLocked(
                resolveStatusId(statementContext?.statement),
              )}
            />
            <div className="attachment-actions" style={{ marginTop: 12 }}>
              {!attachmentEditId ? (
                <Button
                  className={
                    attachmentFile.length === 0 ||
                    attachmentActionLoading ||
                    isStatementLocked(
                      resolveStatusId(statementContext?.statement),
                    )
                      ? "upload-disable-file-btn"
                      : "upload-btn"
                  }
                  type="button"
                  onClick={handleAttachmentUpload}
                  disabled={
                    attachmentFile.length === 0 ||
                    attachmentActionLoading ||
                    isStatementLocked(
                      resolveStatusId(statementContext?.statement),
                    )
                  }
                >
                  {attachmentActionLoading ? "Uploading..." : "Upload File"}
                </Button>
              ) : (
                <>
                  <Button
                    className="upload-btn"
                    type="button"
                    onClick={handleAttachmentUpdate}
                    disabled={
                      attachmentActionLoading ||
                      isStatementLocked(
                        resolveStatusId(statementContext?.statement),
                      )
                    }
                  >
                    {attachmentActionLoading ? "Updating..." : "Update"}
                  </Button>
                  <Button
                    className="attachments-cancel"
                    type="button"
                    onClick={resetAttachmentEditor}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="attachments-list">
            {attachmentLoading ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <CircularProgress disableShrink />
              </Box>
            ) : (
              attachmentFileObject.map((item, idx) => (
                <div
                  key={`${item.attachmentId || item.FileName}-${idx}`}
                  className="attachment-item"
                >
                  <div className="attachment-main">
                    <div className="attachment-content">
                      <strong className="upload-file-name">
                        {item.Path ? (
                          <a
                            href={item.Path}
                            target="_blank"
                            rel="noreferrer"
                            className="attachment-file-link"
                          >
                            {item.FileName}
                          </a>
                        ) : (
                          item.FileName
                        )}
                      </strong>
                      <p className="upload-file-text">
                        {item.Description || "No Remarks ..."}
                      </p>
                      <div className="attachment-meta">
                        <span className="attachment-user">
                          <span className="material-symbols-outlined attachment-user-symbol">
                            person
                          </span>
                          <span className="upload-name-btn">
                            {item.UserName ? item.UserName : "Unknown User"}
                          </span>
                        </span>
                        <span className="attachment-date upload-name-btn">
                          {item.UploadDate
                            ? moment.utc(item.UploadDate).format("MM/DD/YYYY")
                            : ""}
                        </span>
                      </div>
                    </div>
                    <div className="attachment-actions">
                      <IconButton
                        type="button"
                        className={
                          isStatementLocked(
                            resolveStatusId(statementContext?.statement),
                          )
                            ? "disable-file-edit"
                            : "file-edit"
                        }
                        onClick={() => handleAttachmentEdit(item)}
                        disabled={isStatementLocked(
                          resolveStatusId(statementContext?.statement),
                        )}
                        aria-label="edit"
                      >
                        <EditIcon />
                        {/* <img
                          // className="icon-btn-icon"
                          src={editDailyIcon}
                          alt=""
                        /> */}
                      </IconButton>
                      <IconButton
                        type="button"
                        className={
                          isStatementLocked(
                            resolveStatusId(statementContext?.statement),
                          )
                            ? "disable-file-delete"
                            : "file-delete"
                        }
                        onClick={() =>
                          handleAttachmentDelete(item.attachmentId)
                        }
                        disabled={isStatementLocked(
                          resolveStatusId(statementContext?.statement),
                        )}
                        aria-label="delete"
                      >
                        <UploadDeleteIcon />
                        {/* <img
                          // className="icon-btn-icon"
                          src={deleteDailyIcon}
                          alt=""
                        /> */}
                      </IconButton>
                    </div>
                  </div>
                </div>
              ))
            )}
            {!attachmentLoading && attachmentFileObject.length === 0 ? (
              <p className="upload-file-text">
                No attachments found. Click the button above to upload
                attachments to this billing entry.
              </p>
            ) : null}
            <button
              style={{
                // mt: "8px",
                // position: "absolute",
                // bottom: "15px",
                border: "none",
                maxWidth: "75px",
                marginTop: "auto",
                cursor: "pointer",
              }}
              className="cancel-btn"
              type="button"
              onClick={closeAttachmentsModal}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderModal = () => (
    <div className="daily-entry-modal">
      <div
        className="daily-entry-modal-backdrop"
        // onClick={handleBillingCancel}
      />
      <div className="daily-entry-modal-card">
        {popupLoading ? (
          <Box sx={{ py: 10, textAlign: "center" }}>
            <CircularProgress disableShrink />
          </Box>
        ) : (
          <div style={{ position: "relative" }}>
            <div
              style={{
                pointerEvents:
                  isDeleting || isPopupRefreshing ? "none" : "auto",
                opacity: isDeleting || isPopupRefreshing ? 0.65 : 1,
              }}
            >
              <>
                <div className="daily-entry-modal-header">
                  <h2>Add Billing</h2>
                  <div className="billing-date-chip">
                    <span>Billing date</span>
                    <span className="chip">
                      {formatDisplayDate(selectedDate)}
                    </span>
                  </div>
                </div>
                <div className="billing-summary">
                  <div
                    className="billing-summary-left"
                    style={{
                      "--billing-progress-scale": Math.min(
                        initiatedTotals.hours / DAILY_HOUR_LIMIT,
                        1,
                      ),
                      // Initiated (fill) is dark, pending (track) is light.
                      "--billing-progress-fill":
                        initiatedTotals.hours > DAILY_HOUR_LIMIT
                          ? "#FF3B30"
                          : "#2E7D32",
                      "--billing-progress-track":
                        initiatedTotals.hours > DAILY_HOUR_LIMIT
                          ? "#F6D6D3"
                          : "#DDECD9",
                    }}
                  >
                    <div>
                      <p>Initiated hours</p>
                      <strong
                        className={
                          initiatedTotals.hours > DAILY_HOUR_LIMIT
                            ? "is-warning"
                            : ""
                        }
                      >
                        {formatHoursSummary(initiatedTotals.hours)}
                      </strong>
                    </div>
                    <div
                      className="billing-progress"
                      style={{ textAlign: "end" }}
                    >
                      <p>Total hours per day</p>
                      <strong>{`${formatHoursSummary(DAILY_HOUR_LIMIT)}`}</strong>
                      <div className="billing-progress-bar">
                        <span
                          style={{
                            width: `${Math.min(
                              (initiatedTotals.hours / DAILY_HOUR_LIMIT) * 100,
                              100,
                            )}%`,
                            backgroundColor:
                              initiatedTotals.hours >= DAILY_HOUR_LIMIT
                                ? "#FF3B30"
                                : "#4CAF50",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="billing-total">
                    <span>Billing Total:</span>
                    <strong>{formatCurrency(initiatedTotals.amount)}</strong>
                    <span className="billing-total-bar" />
                  </div>
                </div>
                {initiatedTotals.hours > DAILY_HOUR_LIMIT && (
                  <div className="billing-warning-exceed-hours">
                    <strong>Warning</strong>
                    <p>
                      You have entered more than 8 hours for a day. Please
                      review your time entry, as the daily limit is 8 working
                      hours.
                    </p>
                  </div>
                )}
                {flatSavedSections.length > 0 ? (
                  <div
                    className="billing-form"
                    style={{ marginBottom: "16px" }}
                  >
                    {flatSavedSections.map((section, sectionIndex) => (
                      <div
                        key={
                          section.sectionKey ||
                          `${section.projectId}-${section.statement.billingStatementId}-${sectionIndex}`
                        }
                        style={{
                          marginBottom: "24px",
                          border: "1px solid #e7ebf0",
                          borderRadius: "12px",
                          padding: "16px",
                          backgroundColor: "#fff",
                        }}
                      >
                        {(() => {
                          const sectionEditable =
                            isSavedSectionEditable(section);
                          const canRemoveSection =
                            canRemoveSavedSection(section);
                          const savedSectionStatusId =
                            getSavedSectionStatusId(section);
                          const isDraftSection =
                            getSectionDetailId(section) === 0;
                          const sectionStatementId = Number(
                            section?.statement?.billingStatementId || 0,
                          );
                          const hasSectionPeriod = Boolean(
                            String(section?.periodLabel || "").trim(),
                          );
                          const showSectionStatementMeta =
                            sectionStatementId > 0;
                          const draftTouchKey = getDraftSectionTouchKey(
                            section,
                            sectionIndex,
                          );
                          const currentDraftTouched =
                            draftSectionTouched[draftTouchKey] || {};
                          return (
                            <>
                              <div className="billing-form-row header-row">
                                <div className="billing-input with-icon billing-header-autocomplete">
                                  <img
                                    className="input-icon"
                                    src={personIcon}
                                    alt=""
                                  />
                                  {isDraftSection ? (
                                    <Autocomplete
                                      className="billing-autocomplete billing-autocomplete-header"
                                      options={customers}
                                      value={
                                        customers.find(
                                          (customer) =>
                                            String(customer.CustomerID) ===
                                            String(section.customerId),
                                        ) || null
                                      }
                                      onChange={(_, newValue) =>
                                        handleSavedSectionCustomerChange(
                                          sectionIndex,
                                          newValue
                                            ? String(newValue.CustomerID)
                                            : "",
                                        )
                                      }
                                      getOptionLabel={(option) =>
                                        option?.CustomerName || ""
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option.CustomerID === value.CustomerID
                                      }
                                      popupIcon={
                                        <KeyboardArrowDownIcon fontSize="small" />
                                      }
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          placeholder="Select Customer"
                                        />
                                      )}
                                      ListboxProps={{
                                        className:
                                          "billing-autocomplete-listbox",
                                      }}
                                      componentsProps={{
                                        paper: {
                                          className:
                                            "billing-autocomplete-paper",
                                        },
                                      }}
                                    />
                                  ) : (
                                    <div
                                      className="billing-input"
                                      style={{
                                        width: "100%",
                                        paddingLeft: "38px",
                                      }}
                                    >
                                      <input
                                        value={section.customerName}
                                        placeholder="Select Customer"
                                        disabled
                                      />
                                    </div>
                                  )}
                                </div>
                                <div className="billing-input with-icon billing-header-autocomplete">
                                  <img
                                    className="input-icon"
                                    src={personListIcon}
                                    alt=""
                                  />
                                  {isDraftSection ? (
                                    <Autocomplete
                                      className="billing-autocomplete billing-autocomplete-header"
                                      options={section.availableProjects || []}
                                      disabled={!section.customerId}
                                      value={
                                        (section.availableProjects || []).find(
                                          (project) =>
                                            String(
                                              project.ProjectId ??
                                                project.ProjectID,
                                            ) === String(section.projectId),
                                        ) || null
                                      }
                                      onChange={(_, newValue) =>
                                        handleSavedSectionProjectChange(
                                          sectionIndex,
                                          newValue
                                            ? String(
                                                newValue.ProjectId ??
                                                  newValue.ProjectID ??
                                                  "",
                                              )
                                            : "",
                                        )
                                      }
                                      getOptionLabel={(option) =>
                                        option?.Name ||
                                        option?.ProjectName ||
                                        ""
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        String(
                                          option?.ProjectId ??
                                            option?.ProjectID ??
                                            "",
                                        ) ===
                                        String(
                                          value?.ProjectId ??
                                            value?.ProjectID ??
                                            "",
                                        )
                                      }
                                      popupIcon={
                                        <KeyboardArrowDownIcon fontSize="small" />
                                      }
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          placeholder="Select Project"
                                        />
                                      )}
                                      ListboxProps={{
                                        className:
                                          "billing-autocomplete-listbox",
                                      }}
                                      componentsProps={{
                                        paper: {
                                          className:
                                            "billing-autocomplete-paper",
                                        },
                                      }}
                                    />
                                  ) : (
                                    <div
                                      className="billing-input"
                                      style={{
                                        width: "100%",
                                        paddingLeft: "38px",
                                      }}
                                    >
                                      <input
                                        value={section.projectName}
                                        placeholder="Select Project"
                                        disabled
                                      />
                                    </div>
                                  )}
                                </div>
                                <button
                                  className="row-delete-btn"
                                  type="button"
                                  aria-label="Delete billing entry"
                                  onClick={() =>
                                    handleDeleteSavedSection(section)
                                  }
                                  disabled={
                                    isDeleting ||
                                    isDeleteBlockedStatus(
                                      savedSectionStatusId,
                                    ) ||
                                    !canRemoveSection ||
                                    deletingDetailId ===
                                      Number(
                                        section?.item
                                          ?.billingStatementDetailsId || 0,
                                      )
                                  }
                                  style={{
                                    display: canRemoveSection ? "flex" : "none",
                                    cursor:
                                      isDeleting ||
                                      isDeleteBlockedStatus(
                                        savedSectionStatusId,
                                      ) ||
                                      !canRemoveSection
                                        ? "not-allowed"
                                        : "pointer",
                                  }}
                                >
                                  <img src={deleteDailyIcon} alt="" />
                                </button>
                              </div>
                              {isDraftSection &&
                              ((currentDraftTouched.customerId &&
                                !section.customerId) ||
                                (currentDraftTouched.projectId &&
                                  !section.projectId)) ? (
                                <div
                                  className="billing-warning"
                                  style={{ marginTop: "16px" }}
                                >
                                  <p>
                                    {!section.customerId
                                      ? "Customer is required"
                                      : "Project is required"}
                                  </p>
                                </div>
                              ) : null}
                              <div className="billing-table">
                                <div className="billing-table-header">
                                  <span>Billing Type</span>
                                  <span>Day Rate</span>
                                  <span>Hours</span>
                                  <span>Miles</span>
                                  <span>Lodging</span>
                                  <span>Copies</span>
                                </div>
                                {(() => {
                                  const item =
                                    section?.item ||
                                    createBlankLineItem(selectedDayKey);
                                  const itemIndex = 0;
                                  const sectionStatusId = resolveStatusId(
                                    section?.statement,
                                  );
                                  const apiDayRate = parseNumber(
                                    section?.statement?.DayRate ?? 0,
                                  );
                                  const existingDayValue =
                                    section?.displayRates &&
                                    section.displayRates.dayRate != null
                                      ? formatRate(section.displayRates.dayRate)
                                      : item.dayRate;
                                  const displayDayValue =
                                    sectionStatusId === 1
                                      ? existingDayValue
                                      : formatRate(apiDayRate);
                                  return (
                                    <React.Fragment
                                      key={`${section.projectId}-${item?.billingStatementDetailsId || sectionIndex}`}
                                    >
                                      <div className="billing-table-row">
                                        <div>
                                          <label>Billing Type</label>
                                          <div className="billing-type-autocomplete">
                                            <Autocomplete
                                              className="billing-autocomplete"
                                              options={billTypeList}
                                              disabled={!sectionEditable}
                                              value={
                                                billTypeList.find(
                                                  (billType) =>
                                                    String(billType.id) ===
                                                    String(
                                                      item.billingType || "",
                                                    ),
                                                ) || null
                                              }
                                              onChange={(_, newValue) =>
                                                handleSavedSectionFieldValueChange(
                                                  sectionIndex,
                                                  itemIndex,
                                                  "billingType",
                                                  newValue
                                                    ? String(newValue.id)
                                                    : "",
                                                )
                                              }
                                              getOptionLabel={(option) =>
                                                option?.name || ""
                                              }
                                              isOptionEqualToValue={(
                                                option,
                                                value,
                                              ) =>
                                                String(option?.id) ===
                                                String(value?.id)
                                              }
                                              popupIcon={
                                                <KeyboardArrowDownIcon fontSize="small" />
                                              }
                                              renderInput={(params) => (
                                                <TextField
                                                  {...params}
                                                  placeholder="Select"
                                                />
                                              )}
                                              ListboxProps={{
                                                className:
                                                  "billing-autocomplete-listbox",
                                              }}
                                              componentsProps={{
                                                paper: {
                                                  className:
                                                    "billing-autocomplete-paper",
                                                },
                                              }}
                                            />
                                            {isDraftSection &&
                                            currentDraftTouched.billingType &&
                                            !item.billingType ? (
                                              <div
                                                className="billing-warning"
                                                style={{ marginTop: "12px" }}
                                              >
                                                <p>Billing type is required</p>
                                              </div>
                                            ) : null}
                                          </div>
                                        </div>
                                        <div>
                                          <label>Day Rate</label>
                                          <div
                                            className={`billing-input currency ${
                                              !sectionEditable
                                                ? "is-disabled"
                                                : ""
                                            }`}
                                          >
                                            <span>$</span>
                                            <input
                                              type="text"
                                              value={displayDayValue}
                                              disabled
                                            />
                                          </div>
                                        </div>
                                        <div>
                                          <label>Hours</label>
                                          <div
                                            className={`billing-input currency ${
                                              !sectionEditable
                                                ? "is-disabled"
                                                : ""
                                            }`}
                                          >
                                            <input
                                              type="text"
                                              value={item.hours}
                                              onChange={(event) =>
                                                handleSavedSectionHoursChange(
                                                  sectionIndex,
                                                  itemIndex,
                                                  event.target.value,
                                                )
                                              }
                                              disabled={!sectionEditable}
                                            />
                                          </div>
                                        </div>
                                        <div>
                                          <label>Miles</label>
                                          <div
                                            className={`billing-input currency ${
                                              !sectionEditable
                                                ? "is-disabled"
                                                : ""
                                            }`}
                                          >
                                            <input
                                              type="text"
                                              value={item.miles}
                                              onChange={(event) =>
                                                handleSavedSectionIntChange(
                                                  sectionIndex,
                                                  itemIndex,
                                                  "miles",
                                                  event.target.value,
                                                )
                                              }
                                              disabled={!sectionEditable}
                                            />
                                          </div>
                                        </div>
                                        <div>
                                          <label>Lodging</label>
                                          <div
                                            className={`billing-input currency ${
                                              !sectionEditable
                                                ? "is-disabled"
                                                : ""
                                            }`}
                                          >
                                            <span>$</span>
                                            <input
                                              type="text"
                                              value={item.lodging}
                                              onChange={(event) =>
                                                handleSavedSectionFloatChange(
                                                  sectionIndex,
                                                  itemIndex,
                                                  "lodging",
                                                  event.target.value,
                                                )
                                              }
                                              disabled={!sectionEditable}
                                            />
                                          </div>
                                        </div>
                                        <div>
                                          <label>Copies</label>
                                          <div
                                            className={`billing-input currency ${
                                              !sectionEditable
                                                ? "is-disabled"
                                                : ""
                                            }`}
                                          >
                                            <span>$</span>
                                            <input
                                              type="text"
                                              value={item.copies}
                                              onChange={(event) =>
                                                handleSavedSectionFloatChange(
                                                  sectionIndex,
                                                  itemIndex,
                                                  "copies",
                                                  event.target.value,
                                                )
                                              }
                                              disabled={!sectionEditable}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                      <div className="billing-form-row billing-lower-row">
                                        <div className="billing-description-field">
                                          <label>Description</label>
                                          {/* <div className="billing-input billing-description"> */}
                                          <div
                                            className={`billing-input currency textareabox-2 ${
                                              !sectionEditable
                                                ? "is-disabled"
                                                : "billing-input billing-description"
                                            }`}
                                          >
                                            <textarea
                                              value={item.description}
                                              placeholder="Write Description"
                                              onChange={(event) =>
                                                handleSavedSectionFieldValueChange(
                                                  sectionIndex,
                                                  itemIndex,
                                                  "description",
                                                  event.target.value,
                                                )
                                              }
                                              disabled={!sectionEditable}
                                            />
                                          </div>
                                        </div>
                                        <div className="billing-side">
                                          <div className="billing-mini">
                                            <label>Per Diem Rate</label>
                                            <div className="per-diem-rate-wrapper">
                                              {sectionEditable ? (
                                                <Select
                                                  className="dollar-field per-diem-rate-field"
                                                  variant="standard"
                                                  disableUnderline
                                                  disabled={!sectionEditable}
                                                  fullWidth
                                                  displayEmpty
                                                  IconComponent={
                                                    KeyboardArrowDownIcon
                                                  }
                                                  value={
                                                    getSelectedPerDiemOptionValue(
                                                      item,
                                                      section?.perDiemRates ||
                                                        perDiemRates,
                                                    ) || ""
                                                  }
                                                  renderValue={(selected) =>
                                                    selected
                                                      ? resolvePerDiemSelection(
                                                          selected,
                                                          section?.perDiemRates ||
                                                            perDiemRates,
                                                        ).rate
                                                      : ""
                                                  }
                                                  onChange={(event) => {
                                                    const selection =
                                                      resolvePerDiemSelection(
                                                        event.target.value,
                                                        section?.perDiemRates ||
                                                          perDiemRates,
                                                      );
                                                    handleSavedSectionPerDiemRateChange(
                                                      sectionIndex,
                                                      itemIndex,
                                                      selection.rate,
                                                      section?.perDiemRates ||
                                                        perDiemRates,
                                                      selection.type,
                                                    );
                                                  }}
                                                  startAdornment={
                                                    <InputAdornment position="start">
                                                      <span className="dollar-icon">
                                                        $
                                                      </span>
                                                    </InputAdornment>
                                                  }
                                                >
                                                  {getPerDiemMenuItems(
                                                    item,
                                                    section?.perDiemRates ||
                                                      perDiemRates,
                                                    (rawValue) => {
                                                      const selection =
                                                        resolvePerDiemSelection(
                                                          rawValue,
                                                          section?.perDiemRates ||
                                                            perDiemRates,
                                                        );
                                                      handleSavedSectionPerDiemRateChange(
                                                        sectionIndex,
                                                        itemIndex,
                                                        selection.rate,
                                                        section?.perDiemRates ||
                                                          perDiemRates,
                                                        selection.type,
                                                      );
                                                    },
                                                  )}
                                                </Select>
                                              ) : (
                                                <div className="billing-input currency is-disabled">
                                                  <span>$</span>
                                                  <input
                                                    type="text"
                                                    value={formatRate(
                                                      item?.perDiemRate ?? 0,
                                                    )}
                                                    disabled
                                                  />
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                          <div className="billing-mini">
                                            <label>Per Diem</label>
                                            <div className="billing-input currency is-disabled">
                                              <span>$</span>
                                              <input
                                                type="text"
                                                value={item.perDiem}
                                                disabled
                                              />
                                            </div>
                                          </div>
                                          <div className="billing-mini">
                                            <label>Misc</label>
                                            <div
                                              className={`billing-input currency ${
                                                !sectionEditable
                                                  ? "is-disabled"
                                                  : ""
                                              }`}
                                            >
                                              <span>$</span>
                                              <input
                                                type="text"
                                                value={item.misc}
                                                onChange={(event) =>
                                                  handleSavedSectionFloatChange(
                                                    sectionIndex,
                                                    itemIndex,
                                                    "misc",
                                                    event.target.value,
                                                  )
                                                }
                                                disabled={!sectionEditable}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </React.Fragment>
                                  );
                                })()}
                              </div>
                              {hasSectionPeriod ? (
                                <div className="billing-footer-row">
                                  <div style={footerActionBoxStyle}>
                                    {showSectionStatementMeta ? (
                                      <button
                                        className="attachments-btn"
                                        type="button"
                                        onClick={() =>
                                          handleAttachmentOpenForSection(
                                            section,
                                          )
                                        }
                                        disabled={isDeleting}
                                        style={{ margin: 0, cursor: "pointer" }}
                                      >
                                        <img
                                          className="attachments-btn-icon"
                                          src={addBillEditIcon}
                                          alt=""
                                        />
                                        View/Edit Attachments
                                      </button>
                                    ) : null}
                                    {showSectionStatementMeta ? (
                                      <span
                                        className="billing-period-divider"
                                        aria-hidden="true"
                                      />
                                    ) : null}
                                    <span className="billing-period-label">
                                      Billing Period
                                    </span>
                                    <span className="period-chip">
                                      {section.periodLabel}
                                    </span>
                                    {showSectionStatementMeta ? (
                                      <Tooltip
                                        title={`Statement ID: ${
                                          section.statement
                                            ?.billingStatementId ?? "N/A"
                                        }`}
                                        arrow
                                        placement="top"
                                      >
                                        <span>
                                          <button
                                            className="period-view"
                                            type="button"
                                            style={{ cursor: "pointer" }}
                                            disabled={
                                              isDeleting ||
                                              !section.statement
                                                ?.billingStatementId
                                            }
                                            onClick={() =>
                                              section.statement
                                                ?.billingStatementId
                                                ? window.open(
                                                    `/statement/${section.statement.billingStatementId}`,
                                                    "_blank",
                                                  )
                                                : showToast(
                                                    "Please save the billing entry before viewing the statement.",
                                                    "error",
                                                  )
                                            }
                                          >
                                            View
                                            <img
                                              className="period-view-icon"
                                              src={arrowGreen}
                                              alt=""
                                            />
                                          </button>
                                        </span>
                                      </Tooltip>
                                    ) : null}
                                    {showSectionStatementMeta ? (
                                      <span
                                        className={
                                          getStatusChip(
                                            resolveStatusId(section.statement),
                                          ).className
                                        }
                                      >
                                        {
                                          getStatusChip(
                                            resolveStatusId(section.statement),
                                          ).label
                                        }
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              ) : null}
                            </>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                ) : null}
                {hasLineItems && !showManualSection ? (
                  <>
                    <button
                      className="add-more-btn"
                      type="button"
                      onClick={addMoreItem}
                      disabled={isDeleting || isLocked || isAddingMore}
                      style={{
                        cursor:
                          isDeleting || isLocked || isAddingMore
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      Add more time
                    </button>
                    <div className="billing-actions">
                      <button
                        className="primary"
                        type="button"
                        onClick={handleClockHours}
                        disabled={
                          saving || isLocked || !hasPendingEditableEntry
                        }
                        style={{
                          cursor:
                            saving || isLocked || !hasPendingEditableEntry
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>
                      <button
                        className="secondary"
                        type="button"
                        onClick={handleBillingCancel}
                        disabled={isDeleting}
                        style={{
                          cursor: isDeleting ? "not-allowed" : "pointer",
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : null}
                {shouldShowStandaloneForm ? (
                  <form
                    className="billing-form"
                    onSubmit={formik.handleSubmit}
                    style={{
                      marginBottom: "24px",
                      border: "1px solid #e7ebf0",
                      borderRadius: "12px",
                      padding: "16px",
                      backgroundColor: "#fff",
                    }}
                  >
                    <div className="billing-form-row header-row">
                      <div className="billing-input with-icon billing-header-autocomplete">
                        <img className="input-icon" src={personIcon} alt="" />
                        <Autocomplete
                          className="billing-autocomplete billing-autocomplete-header"
                          options={customers}
                          disabled={shouldLockStandaloneHeaderSelection}
                          value={
                            customers.find(
                              (customer) =>
                                String(customer.CustomerID) ===
                                String(formik.values.customerId),
                            ) || null
                          }
                          onChange={(_, newValue) =>
                            handleCustomerChange({
                              target: {
                                value: newValue ? newValue.CustomerID : "",
                              },
                            })
                          }
                          onBlur={formik.handleBlur}
                          getOptionLabel={(option) =>
                            option?.CustomerName || ""
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.CustomerID === value.CustomerID
                          }
                          popupIcon={<KeyboardArrowDownIcon fontSize="small" />}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Customer"
                            />
                          )}
                          ListboxProps={{
                            className: "billing-autocomplete-listbox",
                          }}
                          componentsProps={{
                            paper: { className: "billing-autocomplete-paper" },
                          }}
                        />
                      </div>
                      <div className="billing-input with-icon billing-header-autocomplete">
                        <img
                          className="input-icon"
                          src={personListIcon}
                          alt=""
                        />
                        <Autocomplete
                          className="billing-autocomplete billing-autocomplete-header"
                          options={availableProjects}
                          disabled={
                            shouldLockStandaloneHeaderSelection ||
                            !formik.values.customerId
                          }
                          value={
                            availableProjects.find(
                              (project) =>
                                String(
                                  project.ProjectId ?? project.ProjectID,
                                ) === String(formik.values.projectId),
                            ) || null
                          }
                          onChange={(_, newValue) =>
                            handleProjectChange({
                              target: {
                                value: newValue
                                  ? (newValue.ProjectId ??
                                    newValue.ProjectID ??
                                    "")
                                  : "",
                              },
                            })
                          }
                          onBlur={formik.handleBlur}
                          getOptionLabel={(option) =>
                            option?.Name || option?.ProjectName || ""
                          }
                          isOptionEqualToValue={(option, value) =>
                            String(
                              option?.ProjectId ?? option?.ProjectID ?? "",
                            ) ===
                            String(value?.ProjectId ?? value?.ProjectID ?? "")
                          }
                          popupIcon={<KeyboardArrowDownIcon fontSize="small" />}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Project"
                            />
                          )}
                          ListboxProps={{
                            className: "billing-autocomplete-listbox",
                          }}
                          componentsProps={{
                            paper: { className: "billing-autocomplete-paper" },
                          }}
                        />
                      </div>
                      <button
                        className="row-delete-btn"
                        type="button"
                        aria-label="Delete billing entry"
                        onClick={handleRemoveCurrentFormCard}
                        disabled={isDeleting || visibleCardCount <= 1}
                        style={{
                          display: visibleCardCount > 1 ? "flex" : "none",
                          cursor:
                            isDeleting || visibleCardCount <= 1
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        <img src={deleteDailyIcon} alt="" />
                      </button>
                    </div>
                    {((formik.touched.customerId && formik.errors.customerId) ||
                      (!formik.values.projectId &&
                        formik.touched.projectId &&
                        formik.errors.projectId)) && (
                      <div
                        className="billing-warning"
                        style={{ marginTop: "16px" }}
                      >
                        <p>
                          {formik.errors.customerId || formik.errors.projectId}
                        </p>
                      </div>
                    )}
                    {isLocked ? (
                      <div
                        className="billing-warning"
                        style={{ marginTop: "16px" }}
                      >
                        <p>This statement is closed and cannot be modified.</p>
                      </div>
                    ) : null}
                    <div className="billing-table">
                      <div className="billing-table-header">
                        <span>Billing Type</span>
                        <span>Day Rate</span>
                        <span>Hours</span>
                        <span>Miles</span>
                        <span>Lodging</span>
                        <span>Copies</span>
                      </div>
                      {formik.values.items.map((item, index) => {
                        return (
                          <React.Fragment key={`${selectedDayKey}-${index}`}>
                            <div className="billing-table-row">
                              <div>
                                <label>Billing Type</label>
                                <div className="billing-type-autocomplete">
                                  <Autocomplete
                                    className="billing-autocomplete"
                                    options={billTypeList}
                                    disabled={!isStatementEditable}
                                    value={
                                      billTypeList.find(
                                        (billType) =>
                                          String(billType.id) ===
                                          String(item.billingType || ""),
                                      ) || null
                                    }
                                    onChange={(_, newValue) =>
                                      handleFieldValueChange(
                                        index,
                                        "billingType",
                                        newValue ? String(newValue.id) : "",
                                      )
                                    }
                                    getOptionLabel={(option) =>
                                      option?.name || ""
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                      String(option.id) === String(value.id)
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder="Select"
                                        onBlur={formik.handleBlur}
                                        name={`items.${index}.billingType`}
                                      />
                                    )}
                                    ListboxProps={{
                                      className: "billing-autocomplete-listbox",
                                    }}
                                    componentsProps={{
                                      paper: {
                                        className: "billing-autocomplete-paper",
                                      },
                                    }}
                                  />
                                  {formik.touched.items?.[index]?.billingType &&
                                    formik.errors.items?.[index]
                                      ?.billingType && (
                                      <div
                                        className="billing-warning"
                                        style={{ marginTop: "12px" }}
                                      >
                                        <p>
                                          {
                                            formik.errors.items[index]
                                              .billingType
                                          }
                                        </p>
                                      </div>
                                    )}
                                </div>
                              </div>
                              <div>
                                <label>Day Rate</label>
                                <div
                                  className={`billing-input currency day-rate-field ${
                                    !isStatementEditable ? "is-disabled" : ""
                                  }`}
                                >
                                  <span>$</span>
                                  <input
                                    type="text"
                                    value={(() => {
                                      const statusId = resolveStatusId(
                                        statementContext?.statement,
                                      );
                                      const apiDayRate = parseNumber(
                                        statementContext?.statement?.DayRate ??
                                          0,
                                      );
                                      const existingDayValue =
                                        Number(
                                          statementContext?.statement
                                            ?.billingStatementId || 0,
                                        ) !== 0 &&
                                        projectRateOverride &&
                                        String(
                                          projectRateOverride.projectId,
                                        ) === String(formik.values.projectId)
                                          ? formatRate(
                                              projectRateOverride.dayRate,
                                            )
                                          : item.dayRate;

                                      return statusId === 1
                                        ? existingDayValue
                                        : formatRate(apiDayRate);
                                    })()}
                                    // onChange={(event) =>
                                    //   handleFloatChange(
                                    //     index,
                                    //     "dayRate",
                                    //     event.target.value,
                                    //   )
                                    // }
                                    onBlur={formik.handleBlur}
                                    disabled
                                  />
                                </div>
                              </div>
                              <div>
                                <label>Hours</label>
                                <div className="billing-input">
                                  <input
                                    type="text"
                                    value={item.hours}
                                    onChange={(event) =>
                                      handleHoursChange(
                                        index,
                                        event.target.value,
                                      )
                                    }
                                    onBlur={formik.handleBlur}
                                    disabled={!isStatementEditable}
                                  />
                                </div>
                              </div>
                              <div>
                                <label>Miles</label>
                                <div className="billing-input">
                                  <input
                                    type="text"
                                    value={item.miles}
                                    onChange={(event) =>
                                      handleIntChange(
                                        index,
                                        "miles",
                                        event.target.value,
                                      )
                                    }
                                    onBlur={formik.handleBlur}
                                    disabled={!isStatementEditable}
                                  />
                                </div>
                              </div>
                              <div>
                                <label>Lodging</label>
                                <div className="billing-input currency">
                                  <span>$</span>
                                  <input
                                    type="text"
                                    value={item.lodging}
                                    onChange={(event) =>
                                      handleFloatChange(
                                        index,
                                        "lodging",
                                        event.target.value,
                                      )
                                    }
                                    onBlur={formik.handleBlur}
                                    disabled={!isStatementEditable}
                                  />
                                </div>
                              </div>
                              <div>
                                <label>Copies</label>
                                <div className="billing-input currency">
                                  <span>$</span>
                                  <input
                                    type="text"
                                    value={item.copies}
                                    onChange={(event) =>
                                      handleFloatChange(
                                        index,
                                        "copies",
                                        event.target.value,
                                      )
                                    }
                                    onBlur={formik.handleBlur}
                                    disabled={!isStatementEditable}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="billing-form-row billing-lower-row">
                              <div className="billing-description-field">
                                <label>Description</label>
                                <div className="billing-input billing-description">
                                  <textarea
                                    value={item.description}
                                    onChange={(event) =>
                                      handleFieldValueChange(
                                        index,
                                        "description",
                                        event.target.value,
                                      )
                                    }
                                    placeholder="Write Description"
                                    disabled={!isStatementEditable}
                                  />
                                </div>
                              </div>
                              <div className="billing-side">
                                <div className="billing-mini">
                                  <label>Per Diem Rate</label>
                                  <div className="per-diem-rate-wrapper">
                                    {isStatementEditable ? (
                                      <Select
                                        className="dollar-field per-diem-rate-field"
                                        variant="standard"
                                        disableUnderline
                                        fullWidth
                                        displayEmpty
                                        IconComponent={KeyboardArrowDownIcon}
                                        value={
                                          getSelectedPerDiemOptionValue(
                                            item,
                                            perDiemRates,
                                          ) || ""
                                        }
                                        renderValue={(selected) =>
                                          selected
                                            ? resolvePerDiemSelection(
                                                selected,
                                                perDiemRates,
                                              ).rate
                                            : ""
                                        }
                                        onChange={(event) => {
                                          const selection =
                                            resolvePerDiemSelection(
                                              event.target.value,
                                              perDiemRates,
                                            );
                                          handlePerDiemRateChange(
                                            index,
                                            selection.rate,
                                            perDiemRates,
                                            selection.type,
                                          );
                                        }}
                                        disabled={!isStatementEditable}
                                        startAdornment={
                                          <InputAdornment position="start">
                                            <span className="dollar-icon">
                                              $
                                            </span>
                                          </InputAdornment>
                                        }
                                      >
                                        {getPerDiemMenuItems(
                                          item,
                                          perDiemRates,
                                          (rawValue) => {
                                            const selection =
                                              resolvePerDiemSelection(
                                                rawValue,
                                                perDiemRates,
                                              );
                                            handlePerDiemRateChange(
                                              index,
                                              selection.rate,
                                              perDiemRates,
                                              selection.type,
                                            );
                                          },
                                        )}
                                      </Select>
                                    ) : (
                                      <div className="billing-input currency is-disabled">
                                        <span>$</span>
                                        <input
                                          type="text"
                                          value={formatRate(
                                            item?.perDiemRate ?? 0,
                                          )}
                                          disabled
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="billing-mini">
                                  <label>Per Diem</label>
                                  <div className="billing-input currency is-disabled">
                                    <span>$</span>
                                    <input
                                      type="text"
                                      value={item.perDiem}
                                      disabled
                                    />
                                  </div>
                                </div>
                                <div className="billing-mini">
                                  <label>Misc</label>
                                  <div
                                    className={`billing-input currency ${
                                      !isStatementEditable ? "is-disabled" : ""
                                    }`}
                                  >
                                    <span>$</span>
                                    <input
                                      type="text"
                                      value={item.misc}
                                      onChange={(event) =>
                                        handleFloatChange(
                                          index,
                                          "misc",
                                          event.target.value,
                                        )
                                      }
                                      disabled={!isStatementEditable}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                    <div className="billing-footer-row">
                      {statementContext?.periodLabel ? (
                        <div style={footerActionBoxStyle}>
                          {hasSavedStatementSelection ? (
                            <button
                              className="attachments-btn"
                              type="button"
                              onClick={handleAttachmentOpen}
                              disabled={isDeleting}
                              style={{ margin: 0, cursor:  "pointer" }}
                            >
                              <img
                                className="attachments-btn-icon"
                                src={addBillEditIcon}
                                alt=""
                              />
                              View/Edit Attachments
                            </button>
                          ) : null}
                          {hasSavedStatementSelection ? (
                            <span
                              className="billing-period-divider"
                              aria-hidden="true"
                            />
                          ) : null}
                          <span className="billing-period-label">
                            Billing Period
                          </span>
                          <span className="period-chip">
                            {statementContext?.periodLabel || "Not available"}
                          </span>
                          {hasSavedStatementSelection ? (
                            <Tooltip
                              title={`Statement ID: ${
                                statementContext?.statement
                                  ?.billingStatementId ?? "N/A"
                              }`}
                              arrow
                              placement="top"
                            >
                              <span>
                                <button
                                  className="period-view"
                                  type="button"
                                  disabled={isDeleting}
                                  onClick={() =>
                                    window.open(
                                      `/statement/${statementContext.statement.billingStatementId}`,
                                      "_blank",
                                    )
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  View
                                  <img
                                    className="period-view-icon"
                                    src={arrowGreen}
                                    alt=""
                                  />
                                </button>
                              </span>
                            </Tooltip>
                          ) : null}
                          {hasSavedStatementSelection ? (
                            <span
                              className={
                                getStatusChip(
                                  resolveStatusId(statementContext?.statement),
                                ).className
                              }
                            >
                              {
                                getStatusChip(
                                  resolveStatusId(statementContext?.statement),
                                ).label
                              }
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <button
                      className="add-more-btn"
                      type="button"
                      onClick={addMoreItem}
                      disabled={isDeleting || isLocked || isAddingMore}
                      style={{
                        cursor:
                          isDeleting || isLocked || isAddingMore
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      Add more time
                    </button>
                    <div className="billing-actions">
                      <button
                        className="primary"
                        type="button"
                        onClick={handleClockHours}
                        disabled={
                          saving || isLocked || !hasPendingEditableEntry
                        }
                        style={{
                          cursor:
                            saving || isLocked || !hasPendingEditableEntry
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>
                      <button
                        className="secondary"
                        type="button"
                        onClick={handleBillingCancel}
                        disabled={isDeleting}
                        style={{
                          cursor: isDeleting ? "not-allowed" : "pointer",
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : null}
              </>
            </div>
            {isDeleting || isPopupRefreshing ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.45)",
                }}
              >
                <CircularProgress disableShrink />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );

  if (loadingAssignments) {
    return (
      <Box sx={{ display: "-webkit-box", height: "100%", overflow: "hidden" }}>
        <Sidebar higlightSide={"7"} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: "18px 20px",
            textAlign: "center",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Navbar title="Daily Entry" backFlag={false} backRoute={""} />
          <Box sx={{ mt: 8 }}>
            <CircularProgress disableShrink />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "-webkit-box", height: "100%", overflow: "hidden" }}>
      <Sidebar higlightSide={"7"} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: "18px 20px",
          height: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Navbar title="Daily Entry" backFlag={false} backRoute={""} />
        <div
          className="daily-entry-card"
          style={{
            pointerEvents: isBillingOpen || isAttachmentsOpen ? "none" : "auto",
            minHeight: 0,
          }}
        >
          <div className="daily-entry-toolbar">
            <div className="daily-entry-summary">
              <div className="daily-entry-summary-group">
                <span className="daily-entry-label">
                  {viewMode === "week"
                    ? "This Week's Total Hrs"
                    : viewMode === "cycle"
                      ? "This Cycle's Total Hrs"
                      : "This Month's Total Hrs"}
                </span>
                <span className="daily-entry-value">
                  {visibleSummary.hours.toFixed(2)}
                </span>
              </div>
              <span className="daily-entry-divider" />
              <div className="daily-entry-summary-group">
                <span className="daily-entry-label">Billing Total</span>
                <span className="daily-entry-value">
                  {formatCurrency(visibleSummary.amount)}
                </span>
              </div>
              <span className="daily-entry-divider" />
            </div>
            <div className="daily-entry-controls">
              <span className="daily-entry-month">
                {viewMode === "week"
                  ? `${formatDisplayDate(weekMeta[0]?.date || selectedDate)} - ${formatDisplayDate(
                      weekMeta[weekMeta.length - 1]?.date || selectedDate,
                    )}`
                  : viewMode === "cycle"
                    ? `${formatDisplayDate(getCyclePeriodForDate(selectedDate).startDate)} - ${formatDisplayDate(
                        getCyclePeriodForDate(selectedDate).endDate,
                      )}`
                    : `${monthNames[currentMonthDate.getMonth()]} ${currentMonthDate.getFullYear()}`}
              </span>
              <div className="daily-entry-nav">
                <button
                  className="daily-entry-nav-btn"
                  type="button"
                  onClick={() => navigateCalendar(-1)}
                >
                  &#8249;
                </button>
                <button
                  className="daily-entry-today-btn"
                  type="button"
                  onClick={() => {
                    setCurrentMonthDate(today);
                    setSelectedDate(today);
                  }}
                >
                  Today
                </button>
                <button
                  className="daily-entry-nav-btn"
                  type="button"
                  onClick={() => navigateCalendar(1)}
                >
                  &#8250;
                </button>
              </div>
              <div className="daily-entry-view-toggle">
                <button
                  className={viewMode === "month" ? "is-active" : ""}
                  type="button"
                  onClick={() => handleViewModeChange("month")}
                >
                  Month
                </button>
                <button
                  className={viewMode === "week" ? "is-active" : ""}
                  type="button"
                  onClick={() => handleViewModeChange("week")}
                >
                  Week
                </button>
                <button
                  className={viewMode === "cycle" ? "is-active" : ""}
                  type="button"
                  onClick={() => handleViewModeChange("cycle")}
                >
                  Cycle
                </button>
              </div>
            </div>
          </div>
          <div className="calendar-shell">
            {calendarLoading ? (
              <div className="calendar-loading-overlay">
                <div className="calendar-loading">
                  <CircularProgress disableShrink size={28} />
                </div>
              </div>
            ) : null}

            {viewMode === "month" || viewMode === "cycle" ? (
              <div className="daily-entry-calendar">
                <div className="calendar-weekdays">
                  {weekDays.map((day) => (
                    <div key={day} className="calendar-weekday">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="calendar-grid">
                  {calendarDays.map((day, index) => (
                    <div
                      key={`${day.dateKey}-${index}`}
                      className={`calendar-cell${day.isCurrentMonth ? "" : " is-outside"}${day.isCycleOutside ? " is-cycle-outside" : ""}${day.dateKey === todayKey ? " is-selected" : ""}${day.meta?.alert ? " is-alert-day" : ""}`}
                      onClick={() =>
                        day.isCurrentMonth &&
                        !day.isCycleOutside &&
                        !isBillingOpen &&
                        !isAttachmentsOpen &&
                        !isDeleting &&
                        openBillingPopup(day.date)
                      }
                    >
                      <div className="calendar-cell-top">
                        <span className="calendar-day">{day.dayNumber}</span>
                        <span className="calendar-amount">
                          {day.meta?.amount || "$0.00"}
                        </span>
                      </div>
                      {day.meta ? (
                        <>
                          <div className="calendar-badges">
                            <span
                              className={`calendar-badge hours${day.meta.alert ? " is-alert" : ""}`}
                            >
                              <span className="badge-dot" />
                              {day.meta.hrs}
                            </span>
                            <span className="calendar-badge count">
                              <span className="badge-doc" />
                              {day.meta.count}
                            </span>
                          </div>
                          <div className="calendar-hours-text">
                            {day.meta.hrs}
                          </div>
                        </>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="daily-entry-calendar week-view">
                <div className="week-grid">
                  {weekMeta.map((day) => (
                    <div
                      key={day.dateKey}
                      className={`week-cell${day.dateKey === todayKey ? " is-highlight" : ""}${day.meta?.alert ? " is-alert-day" : ""}`}
                      onClick={() =>
                        !isBillingOpen &&
                        !isAttachmentsOpen &&
                        !isDeleting &&
                        openBillingPopup(day.date)
                      }
                    >
                      <div className="week-header">
                        <span>{day.label}</span>
                        <span>{day.meta?.amount || "$0.00"}</span>
                      </div>
                      {day.meta?.hrs ? (
                        <div className="calendar-badges">
                          <span
                            className={`calendar-badge hours${day.meta.alert ? " is-alert" : ""}`}
                          >
                            <span className="badge-dot" />
                            {day.meta.hrs}
                          </span>
                          <span className="calendar-badge count">
                            <span className="badge-doc" />
                            {day.meta.count}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </Box>

      {isBillingOpen ? renderModal() : null}
      {isAttachmentsOpen ? renderAttachmentModal() : null}
    </Box>
  );
};

export default DailyEntryPage;
